import React, { useState, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { QRCodeSVG } from 'qrcode.react';
import Cropper from 'react-easy-crop';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Sidebar,
  MainContent,
  ContentCard,
  StatCard,
  Icons
} from './ModernSidebarLayout';
import { downloadIRSWaiverPDF } from '../pdfGenerator';
import {
  subscribeToPreparers,
  subscribeToAppointments,
  subscribeToAvailability,
  addPreparer,
  updatePreparer,
  deletePreparer,
  togglePreparerActive,
  confirmAppointment,
  deleteAppointment,
  toggleCompanyPublished,
  updateCompany,
  subscribeToCompanySignedConsents,
  addTaxReturn,
  updateTaxReturn,
  deleteTaxReturn,
  subscribeToCompanyTaxReturns
} from '../firestoreHelper';
import { sendConfirmationEmail } from '../emailService';

// Menu items for Admin Dashboard
const getAdminMenuItems = (counts = {}) => [
  {
    id: 'preparers',
    label: 'Preparers',
    icon: Icons.User,
    badge: counts.preparers || 0
  },
  {
    id: 'availability',
    label: 'Availability',
    icon: Icons.Clock
  },
  {
    id: 'appointments',
    label: 'Appointments',
    icon: Icons.Calendar,
    badge: counts.appointments || 0
  },
  {
    id: 'divider-1',
    type: 'divider',
    label: 'Documents'
  },
  {
    id: 'documents',
    label: 'Documents',
    icon: Icons.Document,
    badge: counts.documents || 0
  },
  {
    id: 'taxreturns',
    label: 'Tax Returns',
    icon: Icons.DollarSign,
    badge: counts.taxReturns || 0
  },
  {
    id: 'divider-2',
    type: 'divider',
    label: 'Settings'
  },
  {
    id: 'landing',
    label: 'Landing Page',
    icon: Icons.Dashboard
  },
  {
    id: 'qrcode',
    label: 'QR Code',
    icon: Icons.Document
  }
];

function CompanyAdminDashboard({ company, onLogout, onUpdateCompany, onSwitchToPreparer }) {
  const [preparers, setPreparers] = useState([]);
  const [showPreparerSwitchModal, setShowPreparerSwitchModal] = useState(false);
  const [appointments, setAppointments] = useState([]);
  // eslint-disable-next-line no-unused-vars
  const [availability, setAvailability] = useState({});
  const [activeTab, setActiveTab] = useState('preparers');
  
  // Mobile responsive states
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1200);
  const isMobile = windowWidth < 768;
  const isSmallMobile = windowWidth < 600;
  const isTablet = windowWidth < 900;
  
  // Modern sidebar states
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  
  const [showAddPreparerForm, setShowAddPreparerForm] = useState(false);
  const [showEditPreparerForm, setShowEditPreparerForm] = useState(false);
  const [editingPreparer, setEditingPreparer] = useState(null);
  const [showPasswordFor, setShowPasswordFor] = useState({});
  const [showCompanySettings, setShowCompanySettings] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);
  
  // Document consent states
  const [signedConsents, setSignedConsents] = useState([]);
  const [selectedPreparerForDocs, setSelectedPreparerForDocs] = useState('all');
  const [viewingConsent, setViewingConsent] = useState(null);
  
  // Search and filter states for Completed Forms
  const [completedSearchName, setCompletedSearchName] = useState('');
  const [completedSearchDateFrom, setCompletedSearchDateFrom] = useState('');
  const [completedSearchDateTo, setCompletedSearchDateTo] = useState('');
  const [selectedCompletedForms, setSelectedCompletedForms] = useState([]);
  
  // Tax Returns states
  const [taxReturns, setTaxReturns] = useState([]);
  const [taxReturnForm, setTaxReturnForm] = useState({
    clientName: '',
    phone: '',
    amount: '',
    taxYear: new Date().getFullYear() - 1,
    paymentStatus: 'unpaid'
  });
  const [savingTaxReturn, setSavingTaxReturn] = useState(false);
  const [editingTaxReturn, setEditingTaxReturn] = useState(null);
  const [selectedPreparerForTaxReturns, setSelectedPreparerForTaxReturns] = useState('all');
  
  // Photo cropper states
  const [showCropper, setShowCropper] = useState(false);
  const [cropImage, setCropImage] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [cropTarget, setCropTarget] = useState(null);

  const [preparerForm, setPreparerForm] = useState({
    name: '',
    username: '',
    password: '',
    email: '',
    phone: '',
    address: '',
    photo: '',
    bio: '',
    isAdmin: false
  });

  const [companySettings, setCompanySettings] = useState({
    name: company?.name || '',
    email: company?.email || '',
    phone: company?.phone || '',
    address: company?.address || '',
    description: company?.description || '',
    logo: company?.logo || '',
    primaryColor: company?.primaryColor || '#2563eb',
    secondaryColor: company?.secondaryColor || '#1e40af',
    accentColor: company?.accentColor || '#20c997',
    // Landing page sections - enable/disable
    sections: company?.sections || {
      hero: true,
      about: true,
      services: true,
      preparers: true,
      features: true,
      testimonials: false,
      contact: true,
      cta: false
    },
    // Hero Section
    heroTitle: company?.heroTitle || '',
    heroSubtitle: company?.heroSubtitle || '',
    heroImage: company?.heroImage || '',
    heroCTAButtonText: company?.heroCTAButtonText || 'Book an Appointment',
    // About Section
    aboutTitle: company?.aboutTitle || 'About Us',
    aboutText: company?.aboutText || '',
    aboutImage: company?.aboutImage || '',
    // Services Section
    servicesTitle: company?.servicesTitle || 'Our Services',
    servicesSubtitle: company?.servicesSubtitle || 'Professional tax services tailored to your needs',
    servicesList: (company?.servicesList && company.servicesList.length > 0) ? company.servicesList : [
      { icon: '📋', title: 'Individual Tax Returns', description: 'Personal tax preparation for individuals and families' },
      { icon: '🏢', title: 'Business Tax Returns', description: 'Comprehensive tax services for businesses of all sizes' },
      { icon: '📊', title: 'Tax Planning', description: 'Strategic planning to minimize your tax burden' }
    ],
    // Preparers Section
    preparersSectionTitle: company?.preparersSectionTitle || 'Our Tax Preparers',
    preparersSectionSubtitle: company?.preparersSectionSubtitle || 'Choose your preferred preparer to book an appointment',
    // Features Section - Dynamic list
    featuresTitle: company?.featuresTitle || 'Why Choose Us?',
    featuresList: (company?.featuresList && company.featuresList.length > 0) ? company.featuresList : [
      { icon: '👨‍💼', title: 'Expert Preparers', description: 'Certified professionals with years of experience' },
      { icon: '⚡', title: 'Fast Service', description: 'Quick and efficient tax preparation' },
      { icon: '🔒', title: 'Secure Filing', description: 'Your information is protected' },
      { icon: '💰', title: 'Maximize Returns', description: 'Get every deduction you deserve' }
    ],
    // Keep old feature fields for backwards compatibility
    feature1Icon: company?.feature1Icon || '👨‍💼',
    feature1Title: company?.feature1Title || 'Expert Preparers',
    feature1Text: company?.feature1Text || 'Certified professionals with years of experience',
    feature2Icon: company?.feature2Icon || '⚡',
    feature2Title: company?.feature2Title || 'Fast Service',
    feature2Text: company?.feature2Text || 'Quick and efficient tax preparation',
    feature3Icon: company?.feature3Icon || '🔒',
    feature3Title: company?.feature3Title || 'Secure Filing',
    feature3Text: company?.feature3Text || 'Your information is protected',
    feature4Icon: company?.feature4Icon || '💰',
    feature4Title: company?.feature4Title || 'Maximize Returns',
    feature4Text: company?.feature4Text || 'Get every deduction you deserve',
    // Testimonials Section
    testimonialsTitle: company?.testimonialsTitle || 'What Our Clients Say',
    testimonials: company?.testimonials || [],
    // Call to Action Section
    ctaTitle: company?.ctaTitle || 'Ready to Get Started?',
    ctaText: company?.ctaText || 'Book your appointment today and let us handle your taxes.',
    ctaButtonText: company?.ctaButtonText || 'Book Now',
    // Contact Section
    contactTitle: company?.contactTitle || 'Contact Us',
    contactSubtitle: company?.contactSubtitle || 'Have questions? Reach out to us.',
    showMap: company?.showMap || false,
    mapEmbedUrl: company?.mapEmbedUrl || '',
    // Footer
    footerText: company?.footerText || '',
    socialLinks: company?.socialLinks || { facebook: '', twitter: '', instagram: '', linkedin: '' },
    // Custom CSS
    customCSS: company?.customCSS || '',
    // Editor state
    editorTab: 'sections'
  });

  // Utility functions
  const convert24to12Hour = (time24) => {
    const [hours, minutes] = time24.split(':');
    const hour = parseInt(hours);
    const period = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
    return `${hour12}:${minutes} ${period}`;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString + 'T00:00:00');
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${month}/${day}/${year}`;
  };

  // Window resize listener for responsive design
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Subscribe to company data
  useEffect(() => {
    if (!company?.id) return;

    const unsubPreparers = subscribeToPreparers((data) => {
      // Filter to only this company's preparers
      const companyPreparers = data.filter(p => p.companyId === company.id);
      setPreparers(companyPreparers);
    }, company.id);

    const unsubAppointments = subscribeToAppointments((data) => {
      const companyAppointments = data.filter(a => a.companyId === company.id);
      setAppointments(companyAppointments);
    }, company.id);

    const unsubAvailability = subscribeToAvailability((data) => {
      setAvailability(data);
    }, company.id);

    // Subscribe to all signed consents for this company
    const unsubConsents = subscribeToCompanySignedConsents(company.id, (data) => {
      setSignedConsents(data);
    });

    // Subscribe to all tax returns for this company
    const unsubTaxReturns = subscribeToCompanyTaxReturns(company.id, (data) => {
      setTaxReturns(data);
    });

    return () => {
      unsubPreparers();
      unsubAppointments();
      unsubAvailability();
      unsubConsents();
      unsubTaxReturns();
    };
  }, [company?.id]);

  // (Legacy sidebar menu ordering removed - now using modern sidebar)

  // Crop complete callback
  const onCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  // Create cropped image - same as App.js
  const createCroppedImage = async () => {
    if (!cropImage || !croppedAreaPixels) return null;
    
    const image = new Image();
    image.src = cropImage;
    
    return new Promise((resolve) => {
      image.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        
        // Set canvas to 400x400 for profile photo
        canvas.width = 400;
        canvas.height = 400;
        
        // Draw the cropped area
        ctx.drawImage(
          image,
          croppedAreaPixels.x,
          croppedAreaPixels.y,
          croppedAreaPixels.width,
          croppedAreaPixels.height,
          0,
          0,
          400,
          400
        );
        
        // Convert to base64
        const croppedImage = canvas.toDataURL('image/jpeg', 0.9);
        resolve(croppedImage);
      };
    });
  };

  // Handle crop confirm - same as App.js
  const handleCropConfirm = async () => {
    const croppedImage = await createCroppedImage();
    if (croppedImage) {
      if (cropTarget === 'newPreparer' || cropTarget === 'editPreparer') {
        setPreparerForm(prev => ({ ...prev, photo: croppedImage }));
      } else if (cropTarget === 'companyLogo') {
        setCompanySettings(prev => ({ ...prev, logo: croppedImage }));
      }
    }
    // Reset cropper
    setShowCropper(false);
    setCropImage(null);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setCroppedAreaPixels(null);
    setCropTarget(null);
  };

  const handlePhotoUpload = (e, target) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        alert('Photo size should be less than 5MB');
        e.target.value = ''; // Reset file input
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setCropImage(reader.result);
        setCropTarget(target);
        setShowCropper(true);
      };
      reader.readAsDataURL(file);
    }
    // Reset file input so same file can be selected again
    e.target.value = '';
  };

  const handleAddPreparer = async (e) => {
    e.preventDefault();
    
    if (!preparerForm.name.trim() || !preparerForm.username.trim() || !preparerForm.password.trim()) {
      alert('Please fill in Name, Username, and Password');
      return;
    }

    if (preparers.some(p => p.username === preparerForm.username.trim())) {
      alert('Username already exists');
      return;
    }

    try {
      await addPreparer({
        ...preparerForm,
        companyId: company.id,
        companyName: company.name
      });
      setPreparerForm({
        name: '', username: '', password: '', email: '',
        phone: '', address: '', photo: '', bio: '', isAdmin: false
      });
      setShowAddPreparerForm(false);
      alert('✅ Preparer added successfully!');
    } catch (error) {
      alert(`❌ Error: ${error.message}`);
    }
  };

  const handleEditPreparer = (preparer) => {
    setEditingPreparer(preparer);
    setPreparerForm({
      name: preparer.name || '',
      username: preparer.username || '',
      password: preparer.password || '',
      email: preparer.email || '',
      phone: preparer.phone || '',
      address: preparer.address || '',
      photo: preparer.photo || '',
      bio: preparer.bio || '',
      isAdmin: preparer.isAdmin || false
    });
    setShowEditPreparerForm(true);
  };

  const handleUpdatePreparer = async (e) => {
    e.preventDefault();
    
    if (!preparerForm.name.trim() || !preparerForm.username.trim() || !preparerForm.password.trim()) {
      alert('Please fill in Name, Username, and Password');
      return;
    }

    try {
      await updatePreparer(editingPreparer.id, preparerForm);
      setShowEditPreparerForm(false);
      setEditingPreparer(null);
      setPreparerForm({
        name: '', username: '', password: '', email: '',
        phone: '', address: '', photo: '', bio: '', isAdmin: false
      });
      alert('✅ Preparer updated successfully!');
    } catch (error) {
      console.error('Update error:', error);
      alert(`❌ Error: ${error.message}`);
    }
  };

  const handleDeletePreparer = async (preparer) => {
    if (window.confirm(`Are you sure you want to delete "${preparer.name}"?`)) {
      try {
        await deletePreparer(preparer.id);
        alert('✅ Preparer deleted successfully!');
      } catch (error) {
        alert(`❌ Error: ${error.message}`);
      }
    }
  };

  const handleToggleActive = async (preparer) => {
    try {
      await togglePreparerActive(preparer.id, !preparer.active);
    } catch (error) {
      alert(`❌ Error: ${error.message}`);
    }
  };

  const handleConfirmAppointment = async (appointment) => {
    if (appointment.confirmed) {
      alert('This appointment is already confirmed');
      return;
    }

    const preparerDetails = preparers.find(p => p.name === appointment.preparer);
    
    if (appointment.email) {
      try {
        await sendConfirmationEmail(appointment, preparerDetails);
        await confirmAppointment(appointment.id);
        alert(`✅ Confirmed! Email sent to: ${appointment.email}`);
      } catch (error) {
        alert(`❌ Error sending email: ${error.message}`);
      }
    } else {
      try {
        await confirmAppointment(appointment.id);
        alert(`✅ Confirmed! No email on file - contact: ${appointment.phoneNumber}`);
      } catch (error) {
        alert(`❌ Error: ${error.message}`);
      }
    }
  };

  const handleDeleteAppointment = async (id) => {
    if (window.confirm('Delete this appointment?')) {
      try {
        await deleteAppointment(id);
        alert('✅ Appointment deleted successfully!');
      } catch (error) {
        console.error('Delete error:', error);
        alert(`❌ Error deleting appointment: ${error.message || error.code || 'Permission denied. Check Firebase rules.'}`);
      }
    }
  };

  const handleTogglePublished = async () => {
    try {
      await toggleCompanyPublished(company.id, !company.published);
      if (!company.published) {
        alert(`✅ Your landing page is now live!\n\nURL: ${window.location.origin}/company/${company.slug}`);
      }
      if (onUpdateCompany) onUpdateCompany();
    } catch (error) {
      alert(`❌ Error: ${error.message}`);
    }
  };

  const handleUpdateCompanySettings = async (e) => {
    e.preventDefault();
    try {
      await updateCompany(company.id, companySettings);
      setShowCompanySettings(false);
      if (onUpdateCompany) onUpdateCompany();
      alert('✅ Company settings updated!');
    } catch (error) {
      alert(`❌ Error: ${error.message}`);
    }
  };

  // Save Landing Page Builder Settings
  const handleSaveSettings = async () => {
    setSavingSettings(true);
    try {
      const landingPageData = {
        // Logo & Branding
        name: companySettings.name,
        logo: companySettings.logo,
        primaryColor: companySettings.primaryColor,
        secondaryColor: companySettings.secondaryColor,
        accentColor: companySettings.accentColor,
        footerText: companySettings.footerText,
        // Contact Info
        email: companySettings.email,
        phone: companySettings.phone,
        address: companySettings.address,
        description: companySettings.description,
        // Sections Toggle
        sections: companySettings.sections,
        // Hero Section
        heroTitle: companySettings.heroTitle,
        heroSubtitle: companySettings.heroSubtitle,
        heroImage: companySettings.heroImage,
        heroCTAButtonText: companySettings.heroCTAButtonText,
        // About Section
        aboutTitle: companySettings.aboutTitle,
        aboutText: companySettings.aboutText,
        aboutImage: companySettings.aboutImage,
        // Services Section
        servicesTitle: companySettings.servicesTitle,
        servicesSubtitle: companySettings.servicesSubtitle,
        servicesList: companySettings.servicesList,
        // Preparers Section
        preparersSectionTitle: companySettings.preparersSectionTitle,
        preparersSectionSubtitle: companySettings.preparersSectionSubtitle,
        // Features Section - Save both new list and old fields for backwards compatibility
        featuresTitle: companySettings.featuresTitle,
        featuresList: companySettings.featuresList,
        feature1Icon: companySettings.feature1Icon,
        feature1Title: companySettings.feature1Title,
        feature1Text: companySettings.feature1Text,
        feature2Icon: companySettings.feature2Icon,
        feature2Title: companySettings.feature2Title,
        feature2Text: companySettings.feature2Text,
        feature3Icon: companySettings.feature3Icon,
        feature3Title: companySettings.feature3Title,
        feature3Text: companySettings.feature3Text,
        feature4Icon: companySettings.feature4Icon,
        feature4Title: companySettings.feature4Title,
        feature4Text: companySettings.feature4Text,
        // Testimonials Section
        testimonialsTitle: companySettings.testimonialsTitle,
        testimonials: companySettings.testimonials,
        // CTA Section
        ctaTitle: companySettings.ctaTitle,
        ctaText: companySettings.ctaText,
        ctaButtonText: companySettings.ctaButtonText,
        // Contact Section
        contactTitle: companySettings.contactTitle,
        contactSubtitle: companySettings.contactSubtitle,
        showMap: companySettings.showMap,
        mapEmbedUrl: companySettings.mapEmbedUrl,
        // Social Links
        socialLinks: companySettings.socialLinks
      };
      
      await updateCompany(company.id, landingPageData);
      if (onUpdateCompany) onUpdateCompany();
      alert('✅ Landing page settings saved!');
    } catch (error) {
      alert(`❌ Error saving settings: ${error.message}`);
    } finally {
      setSavingSettings(false);
    }
  };

  const copyCompanyUrl = () => {
    const url = `${window.location.origin}/company/${company.slug}`;
    navigator.clipboard.writeText(url);
    alert('✅ URL copied!');
  };

  // Get deployed URL - update this with your actual deployed domain
  const getDeployedUrl = () => {
    const deployedDomain = 'https://taxfile-appointment.netlify.app'; // Change this to your actual deployed domain
    return `${deployedDomain}/company/${company.slug}`;
  };

  const copyDeployedUrl = () => {
    const url = getDeployedUrl();
    navigator.clipboard.writeText(url);
    alert('✅ Deployed URL copied!');
  };

  // ==================== Tax Returns Handlers ====================
  
  const handleTaxReturnFormChange = (e) => {
    const { name, value } = e.target;
    setTaxReturnForm(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmitTaxReturn = async (e) => {
    e.preventDefault();
    if (!taxReturnForm.clientName || !taxReturnForm.amount) {
      alert('Please enter client name and amount');
      return;
    }

    setSavingTaxReturn(true);
    try {
      if (editingTaxReturn) {
        // Update existing
        await updateTaxReturn(editingTaxReturn.id, {
          clientName: taxReturnForm.clientName,
          phone: taxReturnForm.phone,
          amount: parseFloat(taxReturnForm.amount),
          taxYear: parseInt(taxReturnForm.taxYear),
          paymentStatus: taxReturnForm.paymentStatus
        });
        alert('✅ Tax return updated successfully!');
        setEditingTaxReturn(null);
      } else {
        // Add new - get first preparer or use null
        const selectedPreparer = preparers.length > 0 ? preparers[0] : null;
        await addTaxReturn({
          clientName: taxReturnForm.clientName,
          phone: taxReturnForm.phone,
          amount: parseFloat(taxReturnForm.amount),
          taxYear: parseInt(taxReturnForm.taxYear),
          paymentStatus: taxReturnForm.paymentStatus,
          preparerId: selectedPreparer?.id || 'company',
          preparerName: selectedPreparer?.name || company.name,
          companyId: company?.id || null
        });
        alert('✅ Tax return added successfully!');
      }
      
      // Reset form
      setTaxReturnForm({
        clientName: '',
        phone: '',
        amount: '',
        taxYear: new Date().getFullYear() - 1,
        paymentStatus: 'unpaid'
      });
    } catch (error) {
      console.error('Error saving tax return:', error);
      alert('❌ Failed to save tax return. Please try again.');
    } finally {
      setSavingTaxReturn(false);
    }
  };

  const handleEditTaxReturn = (taxReturn) => {
    setEditingTaxReturn(taxReturn);
    setTaxReturnForm({
      clientName: taxReturn.clientName,
      phone: taxReturn.phone || '',
      amount: taxReturn.amount.toString(),
      taxYear: taxReturn.taxYear,
      paymentStatus: taxReturn.paymentStatus
    });
  };

  const handleDeleteTaxReturn = async (id) => {
    if (!window.confirm('Are you sure you want to delete this tax return entry?')) return;
    try {
      await deleteTaxReturn(id);
      alert('✅ Tax return deleted');
    } catch (error) {
      console.error('Error deleting tax return:', error);
      alert('❌ Failed to delete tax return');
    }
  };

  const handleCancelEditTaxReturn = () => {
    setEditingTaxReturn(null);
    setTaxReturnForm({
      clientName: '',
      phone: '',
      amount: '',
      taxYear: new Date().getFullYear() - 1,
      paymentStatus: 'unpaid'
    });
  };

  // Filter tax returns by selected preparer
  const filteredTaxReturns = selectedPreparerForTaxReturns === 'all' 
    ? taxReturns 
    : taxReturns.filter(tr => tr.preparerId === selectedPreparerForTaxReturns);

  // Calculate totals
  const taxReturnsGrandTotal = filteredTaxReturns.reduce((sum, tr) => sum + (tr.amount || 0), 0);
  const taxReturnsPaidTotal = filteredTaxReturns.filter(tr => tr.paymentStatus === 'paid').reduce((sum, tr) => sum + (tr.amount || 0), 0);
  const taxReturnsUnpaidTotal = filteredTaxReturns.filter(tr => tr.paymentStatus === 'unpaid').reduce((sum, tr) => sum + (tr.amount || 0), 0);

  const pendingAppointments = appointments.filter(a => !a.confirmed);
  const confirmedAppointments = appointments.filter(a => a.confirmed);

  return (
    <div className="company-admin-dashboard">
      {/* Photo Cropper Modal - Same as App.js */}
      {showCropper && cropImage && ReactDOM.createPortal(
        <div className="modal-overlay cropper-modal-overlay" style={{ zIndex: 999999 }}>
          <div className="modal-content cropper-modal">
            <div className="modal-header">
              <h2>📷 {cropTarget === 'companyLogo' ? 'Adjust Logo' : 'Adjust Profile Photo'}</h2>
              <button className="close-btn" onClick={() => { setShowCropper(false); setCropImage(null); setCropTarget(null); }}>×</button>
            </div>
            <div className="cropper-container">
              <Cropper
                image={cropImage}
                crop={crop}
                zoom={zoom}
                aspect={1}
                cropShape={cropTarget === 'companyLogo' ? 'rect' : 'round'}
                showGrid={cropTarget === 'companyLogo'}
                onCropChange={setCrop}
                onCropComplete={onCropComplete}
                onZoomChange={setZoom}
              />
            </div>
            <div className="cropper-controls">
              <div className="zoom-control">
                <label>🔍 Zoom:</label>
                <input
                  type="range"
                  min={1}
                  max={3}
                  step={0.1}
                  value={zoom}
                  onChange={(e) => setZoom(Number(e.target.value))}
                  className="zoom-slider"
                />
                <span className="zoom-value">{zoom.toFixed(1)}x</span>
              </div>
              <p className="crop-instruction">Drag to position • Scroll or use slider to zoom</p>
            </div>
            <div className="cropper-actions">
              <button className="btn btn-secondary" onClick={() => { setShowCropper(false); setCropImage(null); setCropTarget(null); }}>
                ✕ Cancel
              </button>
              <button className="btn btn-primary" onClick={handleCropConfirm}>
                ✓ Apply Crop
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Menu counts */}
      {(() => {
        const menuCounts = {
          preparers: preparers.length,
          appointments: appointments.length,
          documents: signedConsents.length,
          taxReturns: taxReturns.length
        };
        const menuItems = getAdminMenuItems(menuCounts);
        
        const footerButtons = [
          // Switch to Preparer button - only show if callback exists and there are preparers
          ...(onSwitchToPreparer && preparers.filter(p => p.active).length > 0 ? [{
            label: 'Preparer View',
            icon: Icons.User,
            onClick: () => setShowPreparerSwitchModal(true),
            highlight: true
          }] : []),
          {
            label: 'Settings',
            icon: Icons.Settings,
            onClick: () => setShowCompanySettings(true)
          },
          {
            label: 'Logout',
            icon: Icons.Logout,
            onClick: onLogout
          }
        ];

        return (
          <>
            {/* Modern Sidebar */}
            <Sidebar
              isOpen={isSidebarOpen}
              onClose={() => setIsSidebarOpen(false)}
              isCollapsed={isSidebarCollapsed}
              onToggleCollapse={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
              menuItems={menuItems}
              activeItem={activeTab}
              onItemClick={setActiveTab}
              footerButtons={footerButtons}
              userInfo={{
                name: company?.name || 'Company',
                role: 'Administrator',
                photo: company?.logo,
                initials: company?.name?.substring(0, 2).toUpperCase() || 'AD'
              }}
              headerInfo={{
                logo: company?.logo ? (
                  <img src={company.logo} alt={company.name} style={{ height: '32px', width: 'auto' }} />
                ) : (
                  <span style={{ fontSize: '20px' }}>🏢</span>
                ),
                title: company?.name || 'Company',
                subtitle: 'Admin Dashboard'
              }}
            />

            {/* Main Content */}
            <MainContent
              isSidebarCollapsed={isSidebarCollapsed}
              onMenuClick={() => setIsSidebarOpen(true)}
              headerTitle="Admin Dashboard"
              headerSubtitle={`Welcome back, ${company?.name || 'Admin'}`}
              companyInfo={{ 
                name: company?.name, 
                logo: company?.logo 
              }}
              headerActions={
                <div style={{ 
                  display: 'flex', 
                  gap: isMobile ? '6px' : '10px', 
                  flexWrap: 'wrap',
                  justifyContent: 'flex-end'
                }}>
                  <button 
                    onClick={handleTogglePublished}
                    title={company.published ? 'Published' : 'Unpublished'}
                    style={{ 
                      fontSize: isMobile ? '0.8rem' : '0.875rem', 
                      padding: isMobile ? '8px 12px' : '10px 18px', 
                      borderRadius: '8px', 
                      background: company.published 
                        ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)' 
                        : 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)', 
                      color: 'white', 
                      border: 'none', 
                      cursor: 'pointer',
                      fontWeight: '600',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      boxShadow: company.published 
                        ? '0 2px 8px rgba(16, 185, 129, 0.3)' 
                        : '0 2px 8px rgba(245, 158, 11, 0.3)',
                      minHeight: '40px'
                    }}
                  >
                    <span>{company.published ? '🌐' : '🔒'}</span>
                    {!isSmallMobile && <span>{company.published ? 'Published' : 'Unpublished'}</span>}
                  </button>
                  <button 
                    onClick={() => setShowCompanySettings(true)}
                    title="Settings"
                    style={{ 
                      fontSize: isMobile ? '0.8rem' : '0.875rem', 
                      padding: isMobile ? '8px 12px' : '10px 18px', 
                      borderRadius: '8px', 
                      background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)', 
                      color: 'white', 
                      border: 'none', 
                      cursor: 'pointer',
                      fontWeight: '600',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      boxShadow: '0 2px 8px rgba(59, 130, 246, 0.3)',
                      minHeight: '40px'
                    }}
                  >
                    <span>⚙️</span>
                    {!isSmallMobile && <span>Settings</span>}
                  </button>
                  <button 
                    onClick={onLogout}
                    title="Logout"
                    style={{ 
                      fontSize: isMobile ? '0.8rem' : '0.875rem', 
                      padding: isMobile ? '8px 12px' : '10px 18px', 
                      borderRadius: '8px', 
                      background: '#f1f5f9', 
                      color: '#64748b', 
                      border: '1px solid #e2e8f0', 
                      cursor: 'pointer',
                      fontWeight: '500',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      minHeight: '40px'
                    }}
                  >
                    {isSmallMobile ? '🚪' : 'Logout'}
                  </button>
                </div>
              }
            >
              {/* Published URL Banner */}
              {company.published && company.slug && (
                <div style={{ 
                  backgroundColor: '#f0fdf4', 
                  borderRadius: isMobile ? '12px' : '14px', 
                  padding: isMobile ? '12px 14px' : '16px 20px', 
                  marginBottom: isMobile ? '12px' : '20px',
                  border: '1px solid #bbf7d0',
                  boxShadow: '0 2px 8px rgba(34, 197, 94, 0.1)'
                }}>
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '8px', 
                    flexWrap: 'wrap', 
                    marginBottom: '6px' 
                  }}>
                    <span style={{ fontSize: '11px' }}>🖥️</span>
                    <a 
                      href={`/company/${company.slug}`} 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      style={{ 
                        fontSize: isMobile ? '11px' : '12px', 
                        wordBreak: 'break-all', 
                        color: '#2563eb',
                        flex: 1,
                        minWidth: 0
                      }}
                    >
                      {isSmallMobile ? `.../${company.slug}` : `${window.location.origin}/company/${company.slug}`}
                    </a>
                    <button 
                      onClick={copyCompanyUrl} 
                      style={{ 
                        fontSize: '10px', 
                        padding: '4px 8px', 
                        borderRadius: '4px', 
                        backgroundColor: '#e2e8f0', 
                        border: 'none', 
                        cursor: 'pointer',
                        whiteSpace: 'nowrap'
                      }}
                    >📋</button>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                    <span style={{ fontSize: '11px' }}>🌐</span>
                    <a 
                      href={getDeployedUrl()} 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      style={{ 
                        fontSize: isMobile ? '11px' : '12px', 
                        wordBreak: 'break-all', 
                        color: '#10b981',
                        flex: 1,
                        minWidth: 0
                      }}
                    >
                      {isSmallMobile ? 'Live URL' : getDeployedUrl()}
                    </a>
                    <button 
                      onClick={copyDeployedUrl} 
                      style={{ 
                        fontSize: '10px', 
                        padding: '4px 8px', 
                        borderRadius: '4px', 
                        backgroundColor: '#10b981', 
                        color: 'white', 
                        border: 'none', 
                        cursor: 'pointer',
                        whiteSpace: 'nowrap'
                      }}
                    >📋</button>
                  </div>
                </div>
              )}

              {/* Stats Grid */}
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: isSmallMobile 
                  ? 'repeat(2, 1fr)' 
                  : isMobile 
                    ? 'repeat(3, 1fr)' 
                    : 'repeat(auto-fit, minmax(140px, 1fr))', 
                gap: isMobile ? '10px' : '14px', 
                marginBottom: isMobile ? '16px' : '24px',
                padding: '4px'
              }}>
                <StatCard
                  title="Preparers"
                  value={preparers.length}
                  icon={Icons.User}
                  color="blue"
                  onClick={() => setActiveTab('preparers')}
                />
                <StatCard
                  title="Pending"
                  value={pendingAppointments.length}
                  icon={Icons.Clock}
                  color="orange"
                  onClick={() => setActiveTab('appointments')}
                />
                <StatCard
                  title="Confirmed"
                  value={confirmedAppointments.length}
                  icon={Icons.Calendar}
                  color="green"
                  onClick={() => setActiveTab('appointments')}
                />
                <StatCard
                  title="Documents"
                  value={signedConsents.length}
                  icon={Icons.Document}
                  color="purple"
                  onClick={() => setActiveTab('documents')}
                />
                <StatCard
                  title="Returns"
                  value={taxReturns.length}
                  icon={Icons.DollarSign}
                  color="gray"
                  onClick={() => setActiveTab('taxreturns')}
                />
              </div>

              {/* Tab Content */}
              <AnimatePresence mode="wait">
                <motion.div
                  key={activeTab}
                  initial={{ opacity: 0, y: 15 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -15 }}
                  transition={{ duration: 0.25, ease: 'easeOut' }}
                  style={{ padding: '4px' }}
                >
                <ContentCard>
                  {/* Preparers Tab */}
                  {activeTab === 'preparers' && (
              <div style={{ padding: isMobile ? '4px' : '12px' }}>
                {/* Section Header */}
                <div className="tfp-section-header">
                  <div className="tfp-section-header-left">
                    <div className="tfp-section-icon blue">👥</div>
                    <div>
                      <h2 className="tfp-section-title">Your Preparers</h2>
                      <p className="tfp-section-subtitle">Manage your tax preparation team</p>
                    </div>
                  </div>
                  <div className="tfp-section-header-right">
                    <button 
                      onClick={() => setShowAddPreparerForm(true)}
                      className={`tfp-btn tfp-btn-primary ${isSmallMobile ? 'tfp-btn-block' : ''}`}
                    >
                      ➕ Add Preparer
                    </button>
                  </div>
                </div>

                {preparers.length === 0 ? (
                  <div className="tfp-empty-state">
                    <div className="tfp-empty-state-icon">👥</div>
                    <h3 className="tfp-empty-state-title">No Preparers Yet</h3>
                    <p className="tfp-empty-state-text">Add your first preparer to start accepting appointments</p>
                    <button 
                      onClick={() => setShowAddPreparerForm(true)}
                      className="tfp-btn tfp-btn-primary tfp-btn-lg"
                    >
                      ➕ Add Your First Preparer
                    </button>
                  </div>
            ) : (
              <div className="tfp-grid-cards">
                {preparers.map(preparer => (
                  <div key={preparer.id} className={`tfp-preparer-card ${!preparer.active ? 'inactive' : ''}`}>
                    {/* Preparer Photo */}
                    <div style={{ position: 'relative', display: 'inline-block', marginBottom: '12px' }}>
                      {preparer.photo ? (
                        <img src={preparer.photo} alt={preparer.name} className="tfp-preparer-photo" />
                      ) : (
                        <div className="tfp-preparer-photo-placeholder">👤</div>
                      )}
                      <span style={{
                        position: 'absolute',
                        bottom: '2px',
                        right: '2px',
                        width: '14px',
                        height: '14px',
                        borderRadius: '50%',
                        border: '3px solid white',
                        background: preparer.active ? '#22c55e' : '#94a3b8',
                      }}></span>
                    </div>
                    
                    {/* Preparer Info */}
                    <h3 className="tfp-preparer-name">{preparer.name}</h3>
                    <p className="tfp-preparer-specialty">
                      <span className="tfp-badge tfp-badge-gray">@{preparer.username}</span>
                    </p>
                    
                    {/* Contact Info */}
                    <div style={{ 
                      background: 'var(--tfp-bg-subtle)', 
                      borderRadius: 'var(--tfp-radius-sm)', 
                      padding: '12px',
                      marginBottom: '16px',
                      textAlign: 'left'
                    }}>
                      {preparer.email && (
                        <p className="tfp-apt-card-detail" style={{ margin: '0 0 6px 0' }}>
                          <span>✉️</span>
                          <span style={{ wordBreak: 'break-all' }}>{preparer.email}</span>
                        </p>
                      )}
                      {preparer.phone && (
                        <p className="tfp-apt-card-detail" style={{ margin: 0 }}>
                          <span>📞</span>
                          <span>{preparer.phone}</span>
                        </p>
                      )}
                    </div>
                    
                    {/* Action Buttons */}
                    <div className="tfp-preparer-actions" style={{ flexWrap: 'wrap' }}>
                      <button
                        onClick={() => handleToggleActive(preparer)}
                        className={`tfp-btn tfp-btn-sm ${preparer.active ? 'tfp-btn-success' : 'tfp-btn-outline'}`}
                        style={{ flex: 1 }}
                      >
                        {preparer.active ? '✅ Active' : '❌ Inactive'}
                      </button>
                      <button
                        onClick={() => handleEditPreparer(preparer)}
                        className="tfp-btn tfp-btn-sm tfp-btn-outline"
                      >
                        ✏️ Edit
                      </button>
                      <button
                        onClick={() => handleDeletePreparer(preparer)}
                        className="tfp-btn tfp-btn-sm tfp-btn-danger"
                      >
                        🗑️
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Preparer Availability Tab */}
        {activeTab === 'availability' && (
          <div style={{ padding: isMobile ? '8px' : '12px' }}>
            {/* Section Header */}
            <div className="tfp-section-header">
              <div className="tfp-section-header-left">
                <div className="tfp-section-icon purple">📆</div>
                <div>
                  <h2 className="tfp-section-title">Preparer Availability</h2>
                  <p className="tfp-section-subtitle">View when your preparers are available</p>
                </div>
              </div>
            </div>

            {preparers.length === 0 ? (
              <div className="tfp-empty-state">
                <div className="tfp-empty-state-icon">👥</div>
                <h3 className="tfp-empty-state-title">No Preparers Yet</h3>
                <p className="tfp-empty-state-text">Add preparers first to view their availability</p>
              </div>
            ) : (
              <>
              <div style={{
                display: 'grid',
                gridTemplateColumns: isSmallMobile ? '1fr' : 'repeat(auto-fill, minmax(320px, 1fr))',
                gap: '18px'
              }}>
                  {preparers.map(preparer => {
                    // Get all availability for this preparer
                    const preparerAvailability = Object.entries(availability)
                      .filter(([key]) => key.startsWith(preparer.name + '_'))
                      .map(([key, timeSlots]) => ({
                        date: key.split('_')[1],
                        timeSlots: timeSlots || []
                      }))
                      .filter(a => a.timeSlots.length > 0)
                      .sort((a, b) => new Date(a.date) - new Date(b.date));

                    // Get only future dates
                    const today = new Date();
                    today.setHours(0, 0, 0, 0);
                    const futureAvailability = preparerAvailability.filter(a => new Date(a.date) >= today);

                    return (
                      <div key={preparer.id} className="availability-card" style={{
                        background: 'white',
                        borderRadius: '16px',
                        padding: '20px',
                        boxShadow: '0 4px 15px rgba(0,0,0,0.08)',
                        border: '1px solid #e2e8f0',
                        transition: 'all 0.3s ease'
                      }}>
                        {/* Preparer Header */}
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '15px',
                          marginBottom: '15px',
                          paddingBottom: '15px',
                          borderBottom: '1px solid #e2e8f0'
                        }}>
                          <div style={{
                            width: '50px',
                            height: '50px',
                            borderRadius: '50%',
                            background: preparer.photo ? 'none' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            overflow: 'hidden',
                            flexShrink: 0
                          }}>
                            {preparer.photo ? (
                              <img src={preparer.photo} alt={preparer.name} style={{width: '100%', height: '100%', objectFit: 'cover'}} />
                            ) : (
                              <span style={{color: 'white', fontSize: '20px'}}>👤</span>
                            )}
                          </div>
                          <div style={{flex: 1}}>
                            <h3 style={{margin: 0, fontSize: '16px', fontWeight: '600', color: '#1e293b'}}>{preparer.name}</h3>
                            <span style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '5px',
                              fontSize: '12px',
                              color: preparer.active ? '#10b981' : '#ef4444',
                              marginTop: '3px'
                            }}>
                              <span style={{
                                width: '8px',
                                height: '8px',
                                borderRadius: '50%',
                                background: preparer.active ? '#10b981' : '#ef4444'
                              }}></span>
                              {preparer.active ? 'Active' : 'Inactive'}
                            </span>
                          </div>
                          <div style={{
                            background: futureAvailability.length > 0 ? '#dcfce7' : '#fef3c7',
                            color: futureAvailability.length > 0 ? '#166534' : '#92400e',
                            padding: '6px 12px',
                            borderRadius: '20px',
                            fontSize: '12px',
                            fontWeight: '600'
                          }}>
                            {futureAvailability.length} {futureAvailability.length === 1 ? 'day' : 'days'}
                          </div>
                        </div>

                        {/* Availability List */}
                        {futureAvailability.length === 0 ? (
                          <div style={{
                            textAlign: 'center',
                            padding: '30px 20px',
                            color: '#94a3b8'
                          }}>
                            <div style={{fontSize: '40px', marginBottom: '10px'}}>📅</div>
                            <p style={{margin: 0, fontSize: '14px'}}>No availability set</p>
                            <p style={{margin: '5px 0 0 0', fontSize: '12px'}}>Preparer needs to set their schedule</p>
                          </div>
                        ) : (
                          <div style={{maxHeight: '280px', overflowY: 'auto', paddingRight: '5px'}}>
                            {futureAvailability.slice(0, 7).map((avail, index) => {
                              const dateObj = new Date(avail.date + 'T00:00:00');
                              const isToday = dateObj.toDateString() === new Date().toDateString();
                              const isTomorrow = dateObj.toDateString() === new Date(Date.now() + 86400000).toDateString();
                              const dayName = isToday ? 'Today' : isTomorrow ? 'Tomorrow' : dateObj.toLocaleDateString('en-US', {weekday: 'short'});
                              const formattedDate = dateObj.toLocaleDateString('en-US', {month: 'short', day: 'numeric'});
                              
                              // Convert time slots to readable format
                              const formatTime = (time24) => {
                                const [hours, minutes] = time24.split(':');
                                const hour = parseInt(hours);
                                const period = hour >= 12 ? 'PM' : 'AM';
                                const hour12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
                                return `${hour12}:${minutes} ${period}`;
                              };

                              return (
                                <div key={avail.date} style={{
                                  display: 'flex',
                                  alignItems: 'flex-start',
                                  gap: '12px',
                                  padding: '12px',
                                  background: isToday ? '#f0fdf4' : index % 2 === 0 ? '#f8fafc' : 'white',
                                  borderRadius: '10px',
                                  marginBottom: '8px',
                                  border: isToday ? '1px solid #86efac' : '1px solid transparent'
                                }}>
                                  {/* Date Badge */}
                                  <div style={{
                                    minWidth: '65px',
                                    textAlign: 'center',
                                    background: isToday ? '#22c55e' : '#6366f1',
                                    color: 'white',
                                    borderRadius: '10px',
                                    padding: '8px 10px'
                                  }}>
                                    <div style={{fontSize: '10px', fontWeight: '500', opacity: 0.9}}>{dayName}</div>
                                    <div style={{fontSize: '14px', fontWeight: '700'}}>{formattedDate}</div>
                                  </div>
                                  
                                  {/* Time Slots */}
                                  <div style={{flex: 1}}>
                                    <div style={{
                                      display: 'flex',
                                      flexWrap: 'wrap',
                                      gap: '6px'
                                    }}>
                                      {avail.timeSlots.sort().map((time, idx) => (
                                        <span key={idx} style={{
                                          background: 'white',
                                          border: '1px solid #e2e8f0',
                                          padding: '4px 10px',
                                          borderRadius: '6px',
                                          fontSize: '12px',
                                          color: '#475569',
                                          fontWeight: '500'
                                        }}>
                                          {formatTime(time)}
                                        </span>
                                      ))}
                                    </div>
                                    <div style={{
                                      marginTop: '8px',
                                      fontSize: '11px',
                                      color: '#94a3b8'
                                    }}>
                                      {avail.timeSlots.length} time slot{avail.timeSlots.length !== 1 ? 's' : ''} available
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                            {futureAvailability.length > 7 && (
                              <div style={{
                                textAlign: 'center',
                                padding: '10px',
                                color: '#6366f1',
                                fontSize: '13px',
                                fontWeight: '500'
                              }}>
                                + {futureAvailability.length - 7} more days
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* Overall Summary */}
                <div style={{
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  borderRadius: '16px',
                  padding: '25px',
                  color: 'white',
                  marginTop: '10px'
                }}>
                  <h3 style={{margin: '0 0 15px 0', fontSize: '18px', fontWeight: '600'}}>📊 Availability Summary</h3>
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                    gap: '20px'
                  }}>
                    <div style={{textAlign: 'center', padding: '15px', background: 'rgba(255,255,255,0.15)', borderRadius: '12px'}}>
                      <div style={{fontSize: '28px', fontWeight: '700'}}>{preparers.filter(p => p.active).length}</div>
                      <div style={{fontSize: '13px', opacity: 0.9}}>Active Preparers</div>
                    </div>
                    <div style={{textAlign: 'center', padding: '15px', background: 'rgba(255,255,255,0.15)', borderRadius: '12px'}}>
                      <div style={{fontSize: '28px', fontWeight: '700'}}>
                        {Object.keys(availability).filter(key => {
                          const date = key.split('_')[1];
                          return new Date(date) >= new Date(new Date().toDateString());
                        }).length}
                      </div>
                      <div style={{fontSize: '13px', opacity: 0.9}}>Available Days</div>
                    </div>
                    <div style={{textAlign: 'center', padding: '15px', background: 'rgba(255,255,255,0.15)', borderRadius: '12px'}}>
                      <div style={{fontSize: '28px', fontWeight: '700'}}>
                        {Object.values(availability).flat().length}
                      </div>
                      <div style={{fontSize: '13px', opacity: 0.9}}>Total Time Slots</div>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {/* Appointments Tab */}
        {activeTab === 'appointments' && (
          <div style={{ padding: isMobile ? '8px' : '12px' }}>
            {/* Section Header */}
            <div className="tfp-section-header">
              <div className="tfp-section-header-left">
                <div className="tfp-section-icon green">📅</div>
                <div>
                  <h2 className="tfp-section-title">All Appointments</h2>
                  <p className="tfp-section-subtitle">Manage your client appointments</p>
                </div>
              </div>
              <div className="tfp-section-header-right">
                <span className="tfp-badge tfp-badge-orange">
                  {pendingAppointments.length} Pending
                </span>
                <span className="tfp-badge tfp-badge-green">
                  {confirmedAppointments.length} Confirmed
                </span>
              </div>
            </div>
            
            {appointments.length === 0 ? (
              <div className="tfp-empty-state">
                <div className="tfp-empty-state-icon">📭</div>
                <h3 className="tfp-empty-state-title">No Appointments Yet</h3>
                <p className="tfp-empty-state-text">Appointments will appear here when clients book</p>
              </div>
            ) : (
              <>
                {/* Desktop Table */}
                <div className="tfp-table-wrap" style={{ display: isMobile ? 'none' : 'block' }}>
                    <table className="tfp-table">
                      <thead>
                        <tr>
                          <th>Client</th>
                          <th>Contact</th>
                          <th>Date & Time</th>
                          <th>Preparer</th>
                          <th>Service</th>
                          <th>Status</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {appointments.map((apt) => (
                          <tr key={apt.id}>
                            <td>
                              <strong>{apt.fullName}</strong>
                            </td>
                            <td>
                              <div className="tfp-text-sm">{apt.email}</div>
                              <small className="tfp-text-muted">{apt.phoneNumber}</small>
                            </td>
                            <td>
                              <div style={{ fontWeight: '500' }}>{formatDate(apt.date)}</div>
                              <small className="tfp-text-muted">{convert24to12Hour(apt.time)}</small>
                            </td>
                            <td>{apt.preparer}</td>
                            <td>{apt.fillingType}</td>
                            <td>
                              <span className={`tfp-badge ${apt.confirmed ? 'tfp-badge-green' : 'tfp-badge-orange'}`}>
                                {apt.confirmed ? '✅ Confirmed' : '⏳ Pending'}
                              </span>
                            </td>
                            <td className="actions">
                              <div style={{ display: 'flex', gap: '8px' }}>
                                {!apt.confirmed && (
                                  <button
                                    onClick={() => handleConfirmAppointment(apt)}
                                    className="tfp-btn tfp-btn-sm tfp-btn-success"
                                  >
                                    ✓ Confirm
                                  </button>
                                )}
                                <button
                                  onClick={() => handleDeleteAppointment(apt.id)}
                                  className="tfp-btn tfp-btn-sm tfp-btn-danger"
                                >
                                  🗑️
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                </div>

                {/* Mobile Cards */}
                <div style={{ 
                  display: isMobile ? 'grid' : 'none', 
                  gridTemplateColumns: isSmallMobile ? '1fr' : 'repeat(auto-fill, minmax(280px, 1fr))',
                  gap: '12px' 
                }}>
                  {appointments.map(apt => (
                    <div key={apt.id} className="tfp-card" style={{ padding: 0, overflow: 'hidden' }}>
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '14px 16px',
                        background: apt.confirmed ? 'var(--tfp-success-bg)' : 'var(--tfp-warning-bg)',
                        borderBottom: '1px solid var(--tfp-border)'
                      }}>
                        <span style={{ fontWeight: '600', fontSize: '0.95rem' }}>{apt.fullName}</span>
                        <span className={`tfp-badge ${apt.confirmed ? 'tfp-badge-green' : 'tfp-badge-orange'}`}>
                          {apt.confirmed ? '✅ Confirmed' : '⏳ Pending'}
                        </span>
                      </div>
                      <div style={{ padding: '14px 16px' }}>
                        <div className="tfp-detail-row" style={{ padding: '6px 0' }}>
                          <span className="tfp-detail-label" style={{ minWidth: 'auto' }}>📅</span>
                          <span className="tfp-detail-value">{formatDate(apt.date)} at {convert24to12Hour(apt.time)}</span>
                        </div>
                        <div className="tfp-detail-row" style={{ padding: '6px 0' }}>
                          <span className="tfp-detail-label" style={{ minWidth: 'auto' }}>👤</span>
                          <span className="tfp-detail-value">{apt.preparer}</span>
                        </div>
                        <div className="tfp-detail-row" style={{ padding: '6px 0' }}>
                          <span className="tfp-detail-label" style={{ minWidth: 'auto' }}>📋</span>
                          <span className="tfp-detail-value">{apt.fillingType}</span>
                        </div>
                        <div className="tfp-detail-row" style={{ padding: '6px 0' }}>
                          <span className="tfp-detail-label" style={{ minWidth: 'auto' }}>✉️</span>
                          <span className="tfp-detail-value" style={{ wordBreak: 'break-all' }}>{apt.email}</span>
                        </div>
                        <div className="tfp-detail-row" style={{ padding: '6px 0', borderBottom: 'none' }}>
                          <span className="tfp-detail-label" style={{ minWidth: 'auto' }}>📞</span>
                          <span className="tfp-detail-value">{apt.phoneNumber}</span>
                        </div>
                      </div>
                      <div style={{
                        display: 'flex',
                        gap: '10px',
                        padding: '12px 16px',
                        background: 'var(--tfp-bg-subtle)',
                        borderTop: '1px solid var(--tfp-border)'
                      }}>
                        {!apt.confirmed && (
                          <button
                            onClick={() => handleConfirmAppointment(apt)}
                            className="tfp-btn tfp-btn-success"
                            style={{ flex: 1 }}
                          >
                            ✓ Confirm
                          </button>
                        )}
                        <button
                          onClick={() => handleDeleteAppointment(apt.id)}
                          className="tfp-btn tfp-btn-danger"
                          style={{ flex: apt.confirmed ? 1 : 'none' }}
                        >
                          🗑️ Delete
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        )}

        {/* Tax Returns Tab */}
        {activeTab === 'taxreturns' && (
          <div style={{ padding: isMobile ? '8px' : '12px' }}>
            {/* Section Header */}
            <div className="tfp-section-header">
              <div className="tfp-section-header-left">
                <div className="tfp-section-icon green">💰</div>
                <div>
                  <h2 className="tfp-section-title">Tax Returns Tracker</h2>
                  <p className="tfp-section-subtitle">Track client tax returns and payments</p>
                </div>
              </div>
            </div>

            {/* Grand Total Summary */}
            <div className="tfp-summary-grid">
              <div className="tfp-summary-box">
                <div className="tfp-summary-label">💵 Grand Total</div>
                <div className="tfp-summary-value">${taxReturnsGrandTotal.toLocaleString('en-US', { minimumFractionDigits: 2 })}</div>
              </div>
              <div className="tfp-summary-box green">
                <div className="tfp-summary-label">✅ Paid</div>
                <div className="tfp-summary-value">${taxReturnsPaidTotal.toLocaleString('en-US', { minimumFractionDigits: 2 })}</div>
              </div>
              <div className="tfp-summary-box red">
                <div className="tfp-summary-label">⏳ Unpaid</div>
                <div className="tfp-summary-value">${taxReturnsUnpaidTotal.toLocaleString('en-US', { minimumFractionDigits: 2 })}</div>
              </div>
              <div className="tfp-summary-box blue">
                <div className="tfp-summary-label">📊 Records</div>
                <div className="tfp-summary-value">{filteredTaxReturns.length}</div>
              </div>
            </div>

            {/* Filter by Preparer */}
            <div className="tfp-filters">
              <label className="tfp-form-label" style={{ margin: 0 }}>👤 Filter by Preparer:</label>
              <select
                value={selectedPreparerForTaxReturns}
                onChange={(e) => setSelectedPreparerForTaxReturns(e.target.value)}
                className="tfp-form-select"
                style={{ minWidth: '200px', width: 'auto' }}
              >
                <option value="all">All Preparers</option>
                {preparers.map(p => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            </div>

            {/* Input Form */}
            <div className="tfp-card" style={{ marginBottom: '20px' }}>
              <h3 style={{ margin: '0 0 20px 0', color: 'var(--tfp-text-primary)', fontSize: '1.1rem', fontWeight: '600' }}>
                {editingTaxReturn ? '✏️ Edit Tax Return' : '➕ Add New Tax Return'}
              </h3>
              <form onSubmit={handleSubmitTaxReturn}>
                <div className="tfp-form-grid">
                  <div className="tfp-form-group">
                    <label className="tfp-form-label">👤 Client Name *</label>
                    <input type="text" name="clientName" value={taxReturnForm.clientName} onChange={handleTaxReturnFormChange} placeholder="Enter client name" required className="tfp-form-input" />
                  </div>
                  <div className="tfp-form-group">
                    <label className="tfp-form-label">📞 Phone</label>
                    <input type="tel" name="phone" value={taxReturnForm.phone} onChange={handleTaxReturnFormChange} placeholder="(555) 123-4567" className="tfp-form-input" />
                  </div>
                  <div className="tfp-form-group">
                    <label className="tfp-form-label">💵 Amount *</label>
                    <input type="number" name="amount" value={taxReturnForm.amount} onChange={handleTaxReturnFormChange} placeholder="0.00" required min="0" step="0.01" className="tfp-form-input" />
                  </div>
                  <div className="tfp-form-group">
                    <label className="tfp-form-label">📅 Tax Year</label>
                    <select name="taxYear" value={taxReturnForm.taxYear} onChange={handleTaxReturnFormChange} className="tfp-form-select">
                      {[...Array(10)].map((_, i) => {
                        const year = new Date().getFullYear() - i;
                        return <option key={year} value={year}>{year}</option>;
                      })}
                    </select>
                  </div>
                  <div className="tfp-form-group">
                    <label className="tfp-form-label">💳 Payment Status</label>
                    <select name="paymentStatus" value={taxReturnForm.paymentStatus} onChange={handleTaxReturnFormChange} className="tfp-form-select">
                      <option value="unpaid">Unpaid</option>
                      <option value="paid">Paid</option>
                    </select>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '10px', marginTop: '20px', flexWrap: 'wrap' }}>
                  <button type="submit" disabled={savingTaxReturn} className="tfp-btn tfp-btn-success" style={{ opacity: savingTaxReturn ? 0.7 : 1 }}>
                    {savingTaxReturn ? '⏳ Saving...' : (editingTaxReturn ? '💾 Update' : '➕ Add Tax Return')}
                  </button>
                  {editingTaxReturn && (
                    <button type="button" onClick={handleCancelEditTaxReturn} className="tfp-btn tfp-btn-ghost">❌ Cancel</button>
                  )}
                </div>
              </form>
            </div>

            {/* Tax Returns Table */}
            <div className="tfp-card" style={{ padding: 0 }}>
              {filteredTaxReturns.length === 0 ? (
                <div className="tfp-empty-state">
                  <div className="tfp-empty-state-icon">📋</div>
                  <h3 className="tfp-empty-state-title">No Tax Returns Yet</h3>
                  <p className="tfp-empty-state-text">Add your first tax return entry using the form above.</p>
                </div>
              ) : (
                <div className="tfp-table-wrap">
                  <table className="tfp-table">
                    <thead>
                      <tr>
                        <th>Client Name</th>
                        <th>Phone</th>
                        <th style={{ textAlign: 'center' }}>Tax Year</th>
                        <th style={{ textAlign: 'right' }}>Amount</th>
                        <th style={{ textAlign: 'center' }}>Status</th>
                        <th>Preparer</th>
                        <th style={{ textAlign: 'center' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredTaxReturns.map((tr, index) => (
                        <tr key={tr.id}>
                          <td style={{ fontWeight: '500' }}>{tr.clientName}</td>
                          <td>{tr.phone || '-'}</td>
                          <td style={{ textAlign: 'center' }}>{tr.taxYear}</td>
                          <td style={{ textAlign: 'right', fontWeight: '600' }}>${tr.amount.toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
                          <td style={{ textAlign: 'center' }}>
                            <span className={`tfp-badge ${tr.paymentStatus === 'paid' ? 'tfp-badge-green' : 'tfp-badge-red'}`}>
                              {tr.paymentStatus === 'paid' ? '✅ Paid' : '⏳ Unpaid'}
                            </span>
                          </td>
                          <td>{tr.preparerName || '-'}</td>
                          <td style={{ textAlign: 'center' }}>
                            <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                              <button onClick={() => handleEditTaxReturn(tr)} className="tfp-btn tfp-btn-primary tfp-btn-sm">✏️ Edit</button>
                              <button onClick={() => handleDeleteTaxReturn(tr.id)} className="tfp-btn tfp-btn-danger tfp-btn-sm">🗑️</button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Landing Page Builder Tab */}
        {activeTab === 'landing' && (
          <div style={{ padding: isMobile ? '8px' : '12px' }}>
            {/* Section Header */}
            <div className="tfp-section-header">
              <div className="tfp-section-header-left">
                <div className="tfp-section-icon orange">🎨</div>
                <div>
                  <h2 className="tfp-section-title">Landing Page Builder</h2>
                  <p className="tfp-section-subtitle">Customize your public booking page</p>
                </div>
              </div>
              <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                <button 
                  onClick={handleSaveSettings}
                  disabled={savingSettings}
                  className="tfp-btn tfp-btn-primary"
                  style={{ opacity: savingSettings ? 0.7 : 1 }}
                >
                  {savingSettings ? '⏳ Saving...' : '💾 Save Changes'}
                </button>
                {company.published && company.slug && (
                  <a 
                    href={`/company/${company.slug}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="tfp-btn tfp-btn-outline"
                  >
                    🔗 Open Live Page
                  </a>
                )}
              </div>
            </div>

            {/* Horizontal Navigation Tabs - Responsive */}
            <div className="tfp-filters">
              {[
                { key: 'sections', icon: '📑', label: 'Sections' },
                { key: 'logo', icon: '🎨', label: 'Logo' },
                { key: 'hero', icon: '🏠', label: 'Hero' },
                { key: 'about', icon: 'ℹ️', label: 'About' },
                { key: 'services', icon: '🛠️', label: 'Services' },
                { key: 'preparers', icon: '👥', label: 'Preparers' },
                { key: 'features', icon: '⭐', label: 'Features' },
                { key: 'testimonials', icon: '💬', label: 'Reviews' },
                { key: 'contact', icon: '📞', label: 'Contact' },
                { key: 'social', icon: '🔗', label: 'Social' }
              ].map(item => (
                <button
                  key={item.key}
                  onClick={() => setCompanySettings(prev => ({ ...prev, editorTab: item.key }))}
                  className={`tfp-filter-btn ${companySettings.editorTab === item.key ? 'active' : ''}`}
                >
                  <span>{item.icon}</span> {item.label}
                </button>
              ))}
            </div>

            {/* Editor Content Panel */}
            <div className="tfp-card" style={{ minHeight: '400px', maxHeight: '70vh', overflowY: 'auto' }}>
              {/* Sections Toggle Panel */}
              {companySettings.editorTab === 'sections' && (
                <div className="editor-content">
                  <div className="editor-content-header">
                    <h3>📑 Toggle Sections</h3>
                    <p>Enable or disable sections on your landing page</p>
                  </div>
                  <div className="sections-grid">
                    {[
                      { key: 'hero', icon: '🏠', label: 'Hero Section', desc: 'Main banner with title, subtitle and CTA button' },
                      { key: 'about', icon: 'ℹ️', label: 'About Section', desc: 'Tell visitors about your company' },
                      { key: 'services', icon: '🛠️', label: 'Services Section', desc: 'Showcase your tax services' },
                      { key: 'preparers', icon: '👥', label: 'Preparers Section', desc: 'Display your team members' },
                      { key: 'features', icon: '⭐', label: 'Features Section', desc: 'Highlight why clients should choose you' },
                      { key: 'testimonials', icon: '💬', label: 'Testimonials', desc: 'Show client reviews and ratings' },
                      { key: 'contact', icon: '📞', label: 'Contact Section', desc: 'Contact info with optional map' },
                      { key: 'cta', icon: '📢', label: 'Call to Action', desc: 'Eye-catching banner to drive bookings' }
                    ].map(section => (
                      <div 
                        key={section.key} 
                        className={`section-toggle-card ${companySettings.sections?.[section.key] ? 'enabled' : 'disabled'}`}
                        onClick={() => setCompanySettings(prev => ({
                          ...prev,
                          sections: { ...(prev.sections || {}), [section.key]: !(prev.sections?.[section.key]) }
                        }))}
                      >
                        <div className="toggle-card-icon">{section.icon}</div>
                        <div className="toggle-card-content">
                          <span className="toggle-card-label">{section.label}</span>
                          <span className="toggle-card-desc">{section.desc}</span>
                        </div>
                        <div className={`toggle-card-status ${companySettings.sections?.[section.key] ? 'on' : 'off'}`}>
                          {companySettings.sections?.[section.key] ? '✓ ON' : '○ OFF'}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Hero Section Editor */}
              {companySettings.editorTab === 'hero' && (
                <div className="editor-content">
                  <div className="editor-content-header">
                    <h3>🏠 Hero Banner</h3>
                    <p>Customize your main landing banner</p>
                  </div>
                  <div className="editor-form">
                    <div className="form-group">
                      <label>Hero Title</label>
                        <input
                          type="text"
                          value={companySettings.heroTitle}
                          onChange={(e) => setCompanySettings(prev => ({ ...prev, heroTitle: e.target.value }))}
                          placeholder="Welcome to Our Tax Services"
                        />
                      </div>
                      <div className="form-group">
                        <label>Hero Subtitle</label>
                        <textarea
                          value={companySettings.heroSubtitle}
                          onChange={(e) => setCompanySettings(prev => ({ ...prev, heroSubtitle: e.target.value }))}
                          placeholder="Professional tax preparation services for individuals and businesses"
                          rows="3"
                        />
                      </div>
                      <div className="form-group">
                        <label>Background Image URL</label>
                        <input
                          type="text"
                          value={companySettings.heroImage}
                          onChange={(e) => setCompanySettings(prev => ({ ...prev, heroImage: e.target.value }))}
                          placeholder="https://example.com/hero-image.jpg"
                        />
                        <small className="form-hint">Leave empty for gradient background</small>
                      </div>
                      <div className="form-group">
                        <label>CTA Button Text</label>
                        <input
                          type="text"
                          value={companySettings.heroCTAButtonText}
                          onChange={(e) => setCompanySettings(prev => ({ ...prev, heroCTAButtonText: e.target.value }))}
                          placeholder="Book an Appointment"
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* About Section Editor */}
                {companySettings.editorTab === 'about' && (
                  <div className="editor-content">
                    <div className="editor-content-header">
                      <h3>ℹ️ About Us</h3>
                      <p>Tell visitors about your company</p>
                    </div>
                    <div className="editor-form">
                      <div className="form-group">
                        <label>Section Title</label>
                        <input
                          type="text"
                          value={companySettings.aboutTitle}
                          onChange={(e) => setCompanySettings(prev => ({ ...prev, aboutTitle: e.target.value }))}
                          placeholder="About Our Company"
                        />
                      </div>
                      <div className="form-group">
                        <label>Description</label>
                        <textarea
                          value={companySettings.aboutText}
                          onChange={(e) => setCompanySettings(prev => ({ ...prev, aboutText: e.target.value }))}
                          placeholder="We have been providing professional tax services since..."
                          rows="5"
                        />
                      </div>
                      <div className="form-group">
                        <label>About Image URL</label>
                        <input
                          type="text"
                          value={companySettings.aboutImage}
                          onChange={(e) => setCompanySettings(prev => ({ ...prev, aboutImage: e.target.value }))}
                          placeholder="https://example.com/about-image.jpg"
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Services Section Editor */}
                {companySettings.editorTab === 'services' && (
                  <div className="editor-content">
                    <div className="editor-content-header">
                      <h3>🛠️ Services</h3>
                      <p>List your tax preparation services</p>
                    </div>
                    <div className="editor-form">
                      <div className="form-row-2">
                        <div className="form-group">
                          <label>Section Title</label>
                          <input
                            type="text"
                            value={companySettings.servicesTitle}
                            onChange={(e) => setCompanySettings(prev => ({ ...prev, servicesTitle: e.target.value }))}
                            placeholder="Our Services"
                          />
                        </div>
                        <div className="form-group">
                          <label>Section Subtitle</label>
                          <input
                            type="text"
                            value={companySettings.servicesSubtitle}
                            onChange={(e) => setCompanySettings(prev => ({ ...prev, servicesSubtitle: e.target.value }))}
                            placeholder="Professional tax solutions"
                          />
                        </div>
                      </div>
                      
                      <div className="items-editor">
                        <label>
                          Services List 
                          <span className="items-count">{(companySettings.servicesList || []).length} items</span>
                        </label>
                        {(companySettings.servicesList || []).length === 0 && (
                          <div style={{ padding: '20px', background: '#f8f9fa', borderRadius: '8px', marginBottom: '15px', textAlign: 'center' }}>
                            <p style={{ color: '#6c757d', margin: 0 }}>No services added yet. Click the button below to add your first service.</p>
                          </div>
                        )}
                        <div 
                          style={{ 
                            padding: '12px',
                            border: '2px solid #667eea',
                            borderRadius: '10px',
                            background: '#f8fafc',
                            marginBottom: '12px'
                          }}
                        >
                          {(companySettings.servicesList || []).map((service, index) => (
                            <div key={index} style={{
                              display: 'flex',
                              flexWrap: 'wrap',
                              gap: '10px',
                              alignItems: 'center',
                              marginBottom: '12px',
                              padding: '14px',
                              background: 'white',
                              borderRadius: '10px',
                              border: '1px solid #e2e8f0',
                              flexShrink: 0
                            }}>
                              <span style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                minWidth: '28px',
                                height: '28px',
                                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                color: 'white',
                                fontSize: '0.8rem',
                                fontWeight: '600',
                                borderRadius: '50%'
                              }}>{index + 1}</span>
                              <input
                                type="text"
                                value={service.icon || ''}
                                onChange={(e) => {
                                  const newList = [...(companySettings.servicesList || [])];
                                  newList[index].icon = e.target.value;
                                  setCompanySettings(prev => ({ ...prev, servicesList: newList }));
                                }}
                                placeholder="📋"
                                style={{ width: '50px', textAlign: 'center', fontSize: '1.2em' }}
                              />
                              <input
                                type="text"
                                value={service.title || ''}
                                onChange={(e) => {
                                  const newList = [...(companySettings.servicesList || [])];
                                  newList[index].title = e.target.value;
                                  setCompanySettings(prev => ({ ...prev, servicesList: newList }));
                                }}
                                placeholder="Service Title"
                                style={{ flex: '1 1 100px', minWidth: '80px' }}
                              />
                              <input
                                type="text"
                                value={service.description || ''}
                                onChange={(e) => {
                                  const newList = [...(companySettings.servicesList || [])];
                                  newList[index].description = e.target.value;
                                  setCompanySettings(prev => ({ ...prev, servicesList: newList }));
                                }}
                                placeholder="Description..."
                                style={{ flex: '2 1 150px', minWidth: '100px' }}
                              />
                              <button
                                className="btn-icon btn-danger"
                                onClick={() => {
                                  const newList = (companySettings.servicesList || []).filter((_, i) => i !== index);
                                  setCompanySettings(prev => ({ ...prev, servicesList: newList }));
                                }}
                              >
                                🗑️
                              </button>
                            </div>
                          ))}
                        </div>
                        <button
                          className="btn btn-outline btn-sm add-item-btn"
                          onClick={() => {
                            setCompanySettings(prev => ({
                              ...prev,
                              servicesList: [...(prev.servicesList || []), { icon: '📋', title: '', description: '' }]
                            }));
                          }}
                        >
                          ➕ Add Service
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Preparers Section Editor */}
                {companySettings.editorTab === 'preparers' && (
                  <div className="editor-content">
                    <div className="editor-content-header">
                      <h3>👥 Preparers Section</h3>
                      <p>Customize how your preparers section appears</p>
                    </div>
                    <div className="editor-form">
                      <div className="form-group">
                        <label>Section Title</label>
                        <input
                          type="text"
                          value={companySettings.preparersSectionTitle}
                          onChange={(e) => setCompanySettings(prev => ({ ...prev, preparersSectionTitle: e.target.value }))}
                          placeholder="Our Tax Preparers"
                        />
                      </div>
                      <div className="form-group">
                        <label>Section Subtitle</label>
                        <input
                          type="text"
                          value={companySettings.preparersSectionSubtitle}
                          onChange={(e) => setCompanySettings(prev => ({ ...prev, preparersSectionSubtitle: e.target.value }))}
                          placeholder="Choose your preferred preparer to book an appointment"
                        />
                      </div>
                      <div className="info-box">
                        <span className="info-icon">💡</span>
                        <p>To add or edit preparers, go to the <strong>Preparers</strong> tab from the main dashboard menu.</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Features Section Editor - Why Choose Us */}
                {companySettings.editorTab === 'features' && (
                  <div className="editor-content">
                    <div className="editor-content-header">
                      <h3>⭐ Why Choose Us</h3>
                      <p>Highlight why clients should choose you (add as many as you want)</p>
                    </div>
                    <div className="editor-form">
                      <div className="form-group">
                        <label>Section Title</label>
                        <input
                          type="text"
                          value={companySettings.featuresTitle}
                          onChange={(e) => setCompanySettings(prev => ({ ...prev, featuresTitle: e.target.value }))}
                          placeholder="Why Choose Us"
                        />
                      </div>
                      
                      <div className="items-editor">
                        <label>
                          Features List 
                          <span className="items-count">{(companySettings.featuresList || []).length} items</span>
                        </label>
                        <div 
                          style={{ 
                            padding: '12px',
                            border: '2px solid #f59e0b',
                            borderRadius: '10px',
                            background: '#fffbeb',
                            marginBottom: '12px'
                          }}
                        >
                          {(companySettings.featuresList || []).map((feature, index) => (
                            <div key={index} style={{
                              display: 'flex',
                              flexWrap: 'wrap',
                              gap: '10px',
                              alignItems: 'center',
                              marginBottom: '12px',
                              padding: '14px',
                              background: 'white',
                              borderRadius: '10px',
                              border: '1px solid #e2e8f0',
                              flexShrink: 0
                            }}>
                              <span style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                minWidth: '28px',
                                height: '28px',
                                background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                                color: 'white',
                                fontSize: '0.8rem',
                                fontWeight: '600',
                                borderRadius: '50%'
                              }}>{index + 1}</span>
                              <input
                                type="text"
                                value={feature.icon || ''}
                                onChange={(e) => {
                                  const newList = [...(companySettings.featuresList || [])];
                                  newList[index].icon = e.target.value;
                                  setCompanySettings(prev => ({ ...prev, featuresList: newList }));
                                }}
                                placeholder="⭐"
                                style={{ width: '50px', textAlign: 'center', fontSize: '1.2em' }}
                              />
                              <input
                                type="text"
                                value={feature.title || ''}
                                onChange={(e) => {
                                  const newList = [...(companySettings.featuresList || [])];
                                  newList[index].title = e.target.value;
                                  setCompanySettings(prev => ({ ...prev, featuresList: newList }));
                                }}
                                placeholder="Feature Title"
                                style={{ flex: '1 1 100px', minWidth: '80px' }}
                              />
                              <input
                                type="text"
                                value={feature.description || ''}
                                onChange={(e) => {
                                  const newList = [...(companySettings.featuresList || [])];
                                  newList[index].description = e.target.value;
                                  setCompanySettings(prev => ({ ...prev, featuresList: newList }));
                                }}
                                placeholder="Description..."
                                style={{ flex: '2 1 150px', minWidth: '100px' }}
                              />
                              <button
                                className="btn-icon btn-danger"
                                onClick={() => {
                                  const newList = (companySettings.featuresList || []).filter((_, i) => i !== index);
                                  setCompanySettings(prev => ({ ...prev, featuresList: newList }));
                                }}
                              >
                                🗑️
                              </button>
                            </div>
                          ))}
                        </div>
                        <button
                          className="btn btn-outline btn-sm add-item-btn"
                          onClick={() => {
                            setCompanySettings(prev => ({
                              ...prev,
                              featuresList: [...(prev.featuresList || []), { icon: '⭐', title: '', description: '' }]
                            }));
                          }}
                        >
                          ➕ Add Feature
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Testimonials Section Editor */}
                {companySettings.editorTab === 'testimonials' && (
                  <div className="editor-content">
                    <div className="editor-content-header">
                      <h3>💬 Testimonials</h3>
                      <p>Add client reviews and feedback</p>
                    </div>
                    <div className="editor-form">
                      <div className="form-group">
                        <label>Section Title</label>
                        <input
                          type="text"
                          value={companySettings.testimonialsTitle}
                          onChange={(e) => setCompanySettings(prev => ({ ...prev, testimonialsTitle: e.target.value }))}
                          placeholder="What Our Clients Say"
                        />
                      </div>
                      
                      <div className="testimonials-list-editor">
                        {companySettings.testimonials.map((testimonial, index) => (
                          <div key={index} className="testimonial-edit-card">
                            <div className="testimonial-edit-header">
                              <span>Review #{index + 1}</span>
                              <button
                                className="btn-icon btn-danger"
                                onClick={() => {
                                  const newList = companySettings.testimonials.filter((_, i) => i !== index);
                                  setCompanySettings(prev => ({ ...prev, testimonials: newList }));
                                }}
                              >
                                🗑️
                              </button>
                            </div>
                            <div className="form-row-2">
                              <div className="form-group">
                                <label>Client Name</label>
                                <input
                                  type="text"
                                  value={testimonial.name}
                                  onChange={(e) => {
                                    const newList = [...companySettings.testimonials];
                                    newList[index].name = e.target.value;
                                    setCompanySettings(prev => ({ ...prev, testimonials: newList }));
                                  }}
                                  placeholder="John Doe"
                                />
                              </div>
                              <div className="form-group">
                                <label>Rating</label>
                                <select
                                  value={testimonial.rating}
                                  onChange={(e) => {
                                    const newList = [...companySettings.testimonials];
                                    newList[index].rating = parseInt(e.target.value);
                                    setCompanySettings(prev => ({ ...prev, testimonials: newList }));
                                  }}
                                >
                                  <option value="5">⭐⭐⭐⭐⭐</option>
                                  <option value="4">⭐⭐⭐⭐</option>
                                  <option value="3">⭐⭐⭐</option>
                                  <option value="2">⭐⭐</option>
                                  <option value="1">⭐</option>
                                </select>
                              </div>
                            </div>
                            <div className="form-group">
                              <label>Review Text</label>
                              <textarea
                                value={testimonial.text}
                                onChange={(e) => {
                                  const newList = [...companySettings.testimonials];
                                  newList[index].text = e.target.value;
                                  setCompanySettings(prev => ({ ...prev, testimonials: newList }));
                                }}
                                placeholder="Great service! They helped me save money..."
                                rows="2"
                              />
                            </div>
                          </div>
                        ))}
                        <button
                          className="btn btn-outline btn-sm add-item-btn"
                          onClick={() => {
                            setCompanySettings(prev => ({
                              ...prev,
                              testimonials: [...prev.testimonials, { name: '', rating: 5, text: '' }]
                            }));
                          }}
                        >
                          ➕ Add Testimonial
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Contact Section Editor */}
                {companySettings.editorTab === 'contact' && (
                  <div className="editor-content">
                    <div className="editor-content-header">
                      <h3>📞 Contact & CTA</h3>
                      <p>Customize contact section and call-to-action</p>
                    </div>
                    <div className="editor-form">
                      <div className="editor-subsection">
                        <h4>Contact Section</h4>
                        <div className="form-row-2">
                          <div className="form-group">
                            <label>Section Title</label>
                            <input
                              type="text"
                              value={companySettings.contactTitle}
                              onChange={(e) => setCompanySettings(prev => ({ ...prev, contactTitle: e.target.value }))}
                              placeholder="Contact Us"
                            />
                          </div>
                          <div className="form-group">
                            <label>Subtitle</label>
                            <input
                              type="text"
                              value={companySettings.contactSubtitle}
                              onChange={(e) => setCompanySettings(prev => ({ ...prev, contactSubtitle: e.target.value }))}
                              placeholder="Get in touch with us"
                            />
                          </div>
                        </div>
                        <div className="form-group">
                          <label className="checkbox-label-inline">
                            <input
                              type="checkbox"
                              checked={companySettings.showMap}
                              onChange={(e) => setCompanySettings(prev => ({ ...prev, showMap: e.target.checked }))}
                            />
                            <span>Show Google Map</span>
                          </label>
                        </div>
                        {companySettings.showMap && (
                          <div className="form-group">
                            <label>Google Maps Embed URL</label>
                            <input
                              type="text"
                              value={companySettings.mapEmbedUrl}
                              onChange={(e) => setCompanySettings(prev => ({ ...prev, mapEmbedUrl: e.target.value }))}
                              placeholder="https://www.google.com/maps/embed?pb=..."
                            />
                            <small className="form-hint">Google Maps → Share → Embed → Copy src URL</small>
                          </div>
                        )}
                      </div>

                      <div className="editor-subsection">
                        <h4>Call to Action Banner</h4>
                        <div className="form-group">
                          <label>CTA Title</label>
                          <input
                            type="text"
                            value={companySettings.ctaTitle}
                            onChange={(e) => setCompanySettings(prev => ({ ...prev, ctaTitle: e.target.value }))}
                            placeholder="Ready to Get Started?"
                          />
                        </div>
                        <div className="form-group">
                          <label>CTA Text</label>
                          <textarea
                            value={companySettings.ctaText}
                            onChange={(e) => setCompanySettings(prev => ({ ...prev, ctaText: e.target.value }))}
                            placeholder="Book your appointment today!"
                            rows="2"
                          />
                        </div>
                        <div className="form-group">
                          <label>Button Text</label>
                          <input
                            type="text"
                            value={companySettings.ctaButtonText}
                            onChange={(e) => setCompanySettings(prev => ({ ...prev, ctaButtonText: e.target.value }))}
                            placeholder="Book Now"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Logo & Branding Editor */}
                {companySettings.editorTab === 'logo' && (
                  <div className="editor-content">
                    <div className="editor-content-header">
                      <h3>🎨 Logo & Branding</h3>
                      <p>Customize your company logo and brand colors</p>
                    </div>
                    <div className="editor-form">
                      <div className="logo-preview-section">
                        <label className="current-logo-preview logo-upload-label">
                          <input
                            type="file"
                            accept="image/*"
                            onChange={(e) => handlePhotoUpload(e, 'companyLogo')}
                            style={{ display: 'none' }}
                          />
                          {companySettings.logo ? (
                            <>
                              <img src={companySettings.logo} alt="Company Logo" className="logo-preview-img" />
                              <div className="logo-upload-overlay">
                                <span>📷</span>
                                <p>Click to change</p>
                              </div>
                            </>
                          ) : (
                            <div className="logo-placeholder-large">
                              <span>📷</span>
                              <p>Click to upload logo</p>
                            </div>
                          )}
                        </label>
                      </div>
                      <p className="upload-hint">Click the logo area above to upload an image</p>
                      
                      <div className="form-group">
                        <label>Or Enter Logo URL</label>
                        <input
                          type="text"
                          value={companySettings.logo}
                          onChange={(e) => setCompanySettings(prev => ({ ...prev, logo: e.target.value }))}
                          placeholder="https://example.com/your-logo.png"
                        />
                        <small className="form-hint">You can also paste a URL to your logo image</small>
                      </div>

                      <div className="editor-subsection">
                        <h4>Brand Colors</h4>
                        <div className="color-picker-grid">
                          <div className="form-group color-picker-group">
                            <label>Primary Color</label>
                            <div className="color-input-wrapper">
                              <input
                                type="color"
                                value={companySettings.primaryColor}
                                onChange={(e) => setCompanySettings(prev => ({ ...prev, primaryColor: e.target.value }))}
                                className="color-input"
                              />
                              <input
                                type="text"
                                value={companySettings.primaryColor}
                                onChange={(e) => setCompanySettings(prev => ({ ...prev, primaryColor: e.target.value }))}
                                placeholder="#2563eb"
                                className="color-text-input"
                              />
                            </div>
                            <small className="form-hint">Used for buttons, headers, and hero background</small>
                          </div>
                          <div className="form-group color-picker-group">
                            <label>Secondary Color</label>
                            <div className="color-input-wrapper">
                              <input
                                type="color"
                                value={companySettings.secondaryColor}
                                onChange={(e) => setCompanySettings(prev => ({ ...prev, secondaryColor: e.target.value }))}
                                className="color-input"
                              />
                              <input
                                type="text"
                                value={companySettings.secondaryColor}
                                onChange={(e) => setCompanySettings(prev => ({ ...prev, secondaryColor: e.target.value }))}
                                placeholder="#1e40af"
                                className="color-text-input"
                              />
                            </div>
                            <small className="form-hint">Used for gradients and hover states</small>
                          </div>
                          <div className="form-group color-picker-group">
                            <label>Accent Color</label>
                            <div className="color-input-wrapper">
                              <input
                                type="color"
                                value={companySettings.accentColor}
                                onChange={(e) => setCompanySettings(prev => ({ ...prev, accentColor: e.target.value }))}
                                className="color-input"
                              />
                              <input
                                type="text"
                                value={companySettings.accentColor}
                                onChange={(e) => setCompanySettings(prev => ({ ...prev, accentColor: e.target.value }))}
                                placeholder="#20c997"
                                className="color-text-input"
                              />
                            </div>
                            <small className="form-hint">Used for badges, highlights, and special elements</small>
                          </div>
                        </div>
                        <div className="color-preview-bar" style={{ background: `linear-gradient(135deg, ${companySettings.primaryColor}, ${companySettings.secondaryColor})` }}>
                          <span>Preview: Your brand gradient</span>
                        </div>
                        <div className="color-swatches-preview">
                          <div className="color-swatch" style={{ background: companySettings.primaryColor }}>
                            <span>Primary</span>
                          </div>
                          <div className="color-swatch" style={{ background: companySettings.secondaryColor }}>
                            <span>Secondary</span>
                          </div>
                          <div className="color-swatch" style={{ background: companySettings.accentColor }}>
                            <span>Accent</span>
                          </div>
                        </div>
                      </div>

                      <div className="form-group">
                        <label>Company Name</label>
                        <input
                          type="text"
                          value={companySettings.name}
                          onChange={(e) => setCompanySettings(prev => ({ ...prev, name: e.target.value }))}
                          placeholder="Your Company Name"
                        />
                      </div>

                      <div className="form-group">
                        <label>Footer Text</label>
                        <input
                          type="text"
                          value={companySettings.footerText}
                          onChange={(e) => setCompanySettings(prev => ({ ...prev, footerText: e.target.value }))}
                          placeholder="© 2025 Your Company. All rights reserved."
                        />
                        <small className="form-hint">Leave empty for default copyright text</small>
                      </div>
                    </div>
                  </div>
                )}

                {/* Social Links Editor */}
                {companySettings.editorTab === 'social' && (
                  <div className="editor-content">
                    <div className="editor-content-header">
                      <h3>🔗 Social Links</h3>
                      <p>Add your social media profiles</p>
                    </div>
                    <div className="editor-form">
                      <div className="social-links-grid">
                        <div className="form-group social-input-group">
                          <label><span className="social-icon facebook">f</span> Facebook</label>
                          <input
                            type="text"
                            value={companySettings.socialLinks.facebook}
                            onChange={(e) => setCompanySettings(prev => ({
                              ...prev,
                              socialLinks: { ...prev.socialLinks, facebook: e.target.value }
                            }))}
                            placeholder="https://facebook.com/yourcompany"
                          />
                        </div>
                        <div className="form-group social-input-group">
                          <label><span className="social-icon twitter">𝕏</span> Twitter/X</label>
                          <input
                            type="text"
                            value={companySettings.socialLinks.twitter}
                            onChange={(e) => setCompanySettings(prev => ({
                              ...prev,
                              socialLinks: { ...prev.socialLinks, twitter: e.target.value }
                            }))}
                            placeholder="https://twitter.com/yourcompany"
                          />
                        </div>
                        <div className="form-group social-input-group">
                          <label><span className="social-icon instagram">📷</span> Instagram</label>
                          <input
                            type="text"
                            value={companySettings.socialLinks.instagram}
                            onChange={(e) => setCompanySettings(prev => ({
                              ...prev,
                              socialLinks: { ...prev.socialLinks, instagram: e.target.value }
                            }))}
                            placeholder="https://instagram.com/yourcompany"
                          />
                        </div>
                        <div className="form-group social-input-group">
                          <label><span className="social-icon linkedin">in</span> LinkedIn</label>
                          <input
                            type="text"
                            value={companySettings.socialLinks.linkedin}
                            onChange={(e) => setCompanySettings(prev => ({
                              ...prev,
                              socialLinks: { ...prev.socialLinks, linkedin: e.target.value }
                            }))}
                            placeholder="https://linkedin.com/company/yourcompany"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>

            {/* Live Preview Section */}
            <div className="builder-preview-section">
              <div className="preview-header">
                <h3>👁️ Live Preview</h3>
                <span className="preview-hint">Changes appear in real-time</span>
              </div>
              <div className="preview-container">
                <div className="preview-frame">
                  {/* Mini Hero Preview */}
                  {companySettings.sections.hero && (
                    <div 
                      className="preview-hero"
                      style={companySettings.heroImage ? {
                        backgroundImage: `linear-gradient(rgba(0,0,0,0.5), rgba(0,0,0,0.5)), url(${companySettings.heroImage})`,
                        backgroundSize: 'cover'
                      } : { background: `linear-gradient(135deg, ${company.primaryColor || '#3b82f6'}, ${company.secondaryColor || '#1e40af'})` }}
                    >
                      <h4>{companySettings.heroTitle || company.name || 'Your Company Name'}</h4>
                      <p>{companySettings.heroSubtitle || 'Your tagline here'}</p>
                    </div>
                  )}

                  {/* Mini About Preview */}
                  {companySettings.sections.about && (companySettings.aboutTitle || companySettings.aboutText) && (
                    <div className="preview-section preview-about">
                      <div className="preview-section-title">{companySettings.aboutTitle || 'About Us'}</div>
                      <p className="preview-text">{companySettings.aboutText?.substring(0, 100) || 'About description...'}{companySettings.aboutText?.length > 100 ? '...' : ''}</p>
                    </div>
                  )}

                  {/* Mini Services Preview */}
                  {companySettings.sections.services && companySettings.servicesList?.length > 0 && (
                    <div className="preview-section preview-services">
                      <div className="preview-section-title">{companySettings.servicesTitle || 'Our Services'}</div>
                      <div className="preview-services-grid">
                        {companySettings.servicesList.slice(0, 3).map((service, i) => (
                          <div key={i} className="preview-service-item">
                            <span>{service.icon || '📋'}</span>
                            <span>{service.title || 'Service'}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Mini Features Preview */}
                  {companySettings.sections.features && (
                    <div className="preview-section preview-features">
                      <div className="preview-section-title">{companySettings.featuresTitle || 'Why Choose Us'}</div>
                      <div className="preview-features-grid">
                        <div className="preview-feature-item">
                          <span>{companySettings.feature1Icon || '👨‍💼'}</span>
                          <span>{companySettings.feature1Title || 'Feature 1'}</span>
                        </div>
                        <div className="preview-feature-item">
                          <span>{companySettings.feature2Icon || '⚡'}</span>
                          <span>{companySettings.feature2Title || 'Feature 2'}</span>
                        </div>
                        <div className="preview-feature-item">
                          <span>{companySettings.feature3Icon || '🔒'}</span>
                          <span>{companySettings.feature3Title || 'Feature 3'}</span>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Mini Testimonials Preview */}
                  {companySettings.sections.testimonials && companySettings.testimonials?.length > 0 && (
                    <div className="preview-section preview-testimonials">
                      <div className="preview-section-title">{companySettings.testimonialsTitle || 'Testimonials'}</div>
                      <div className="preview-testimonial-item">
                        <span>{'⭐'.repeat(companySettings.testimonials[0]?.rating || 5)}</span>
                        <p>"{companySettings.testimonials[0]?.text?.substring(0, 50) || 'Review text...'}..."</p>
                        <span className="preview-author">— {companySettings.testimonials[0]?.name || 'Client'}</span>
                      </div>
                    </div>
                  )}

                  {/* Mini CTA Preview */}
                  {companySettings.sections.cta && (
                    <div 
                      className="preview-cta"
                      style={{ background: `linear-gradient(135deg, ${company.primaryColor || '#3b82f6'}, ${company.secondaryColor || '#1e40af'})` }}
                    >
                      <h4>{companySettings.ctaTitle || 'Ready to Get Started?'}</h4>
                      <button>{companySettings.ctaButtonText || 'Book Now'}</button>
                    </div>
                  )}

                  {/* Mini Contact Preview */}
                  {companySettings.sections.contact && (
                    <div className="preview-section preview-contact">
                      <div className="preview-section-title">{companySettings.contactTitle || 'Contact Us'}</div>
                      <div className="preview-contact-items">
                        {company.phone && <span>📞 {company.phone}</span>}
                        {company.email && <span>✉️ {company.email}</span>}
                      </div>
                    </div>
                  )}

                  {/* Social Links Preview */}
                  {(companySettings.socialLinks.facebook || companySettings.socialLinks.twitter || companySettings.socialLinks.instagram || companySettings.socialLinks.linkedin) && (
                    <div className="preview-social">
                      {companySettings.socialLinks.facebook && <span className="preview-social-icon">f</span>}
                      {companySettings.socialLinks.twitter && <span className="preview-social-icon">X</span>}
                      {companySettings.socialLinks.instagram && <span className="preview-social-icon">IG</span>}
                      {companySettings.socialLinks.linkedin && <span className="preview-social-icon">in</span>}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* QR Code Tab */}
        {activeTab === 'qrcode' && (
          <div style={{ padding: isMobile ? '8px' : '12px' }}>
            {/* Section Header */}
            <div className="tfp-section-header">
              <div className="tfp-section-header-left">
                <div className="tfp-section-icon purple">📱</div>
                <div>
                  <h2 className="tfp-section-title">Your Booking QR Code</h2>
                  <p className="tfp-section-subtitle">Share this QR code with clients</p>
                </div>
              </div>
            </div>

            {company.published && company.slug ? (
              <div className="tfp-card" style={{ maxWidth: '440px', margin: '0 auto', textAlign: 'center' }}>
                <div style={{ background: 'white', padding: '28px', borderRadius: '18px', display: 'inline-block', marginBottom: '28px', boxShadow: 'var(--tfp-shadow-sm)' }}>
                  <QRCodeSVG 
                    value={getDeployedUrl()}
                    size={220}
                    level="H"
                    includeMargin={true}
                  />
                </div>
                <div className="tfp-alert" style={{ background: 'var(--tfp-success-bg)', borderColor: '#86efac', marginBottom: '12px' }}>
                  <p style={{ margin: 0, fontSize: '0.8rem', color: '#166534', fontWeight: '600' }}>🌐 Live URL</p>
                  <p style={{ margin: '4px 0 0 0', fontSize: '0.85rem', color: '#166534', wordBreak: 'break-all' }}>
                    {getDeployedUrl()}
                  </p>
                </div>
                <p style={{ fontSize: '0.8rem', color: 'var(--tfp-text-muted)', marginBottom: '20px', wordBreak: 'break-all' }}>
                  🖥️ Local: {window.location.origin}/company/{company.slug}
                </p>
                <div style={{ display: 'flex', flexDirection: isSmallMobile ? 'column' : 'row', gap: '12px', marginBottom: '20px' }}>
                  <button onClick={copyDeployedUrl} className="tfp-btn tfp-btn-success" style={{ flex: 1 }}>📋 Copy Live URL</button>
                  <a href={getDeployedUrl()} target="_blank" rel="noopener noreferrer" className="tfp-btn tfp-btn-outline" style={{ flex: 1 }}>🔗 Open Page</a>
                </div>
                <div className="tfp-alert">
                  💡 Print this QR code and display it in your office. Clients can scan it to book appointments!
                </div>
              </div>
            ) : (
              <div className="tfp-empty-state" style={{ border: '2px dashed var(--tfp-border)' }}>
                <div className="tfp-empty-state-icon">🔒</div>
                <h3 className="tfp-empty-state-title">QR Code Not Available</h3>
                <p className="tfp-empty-state-text">Publish your landing page to get a QR code</p>
                <button onClick={handleTogglePublished} className="tfp-btn tfp-btn-primary">🌐 Publish Now</button>
              </div>
            )}
          </div>
        )}

        {/* Documents Tab */}
        {activeTab === 'documents' && (
          <div style={{ padding: isMobile ? '8px' : '12px' }}>
            {/* Section Header */}
            <div className="tfp-section-header">
              <div className="tfp-section-header-left">
                <div className="tfp-section-icon purple">📄</div>
                <div>
                  <h2 className="tfp-section-title">Documents & Forms</h2>
                  <p className="tfp-section-subtitle">Manage client documents and forms</p>
                </div>
              </div>
            </div>

            {/* IRS Waiver Form Section - Main Feature */}
            <div className="tfp-feature-card">
              <div className="tfp-feature-card-header brown">
                <div className="tfp-feature-card-icon">📋</div>
                <div>
                  <h3 className="tfp-feature-card-title">IRS Notice 2014-7 Waiver Form</h3>
                  <p className="tfp-feature-card-subtitle">Complete intake form for Medicaid waiver clients</p>
                </div>
              </div>
              
              <div className="tfp-feature-card-body">
                <div className="tfp-chip-grid">
                  <div className="tfp-chip amber">📝 Client Information</div>
                  <div className="tfp-chip blue">👥 Spouse & Dependents</div>
                  <div className="tfp-chip green">✍️ E-Signature Ready</div>
                  <div className="tfp-chip purple">🪪 Photo ID Upload</div>
                </div>

                <div className="tfp-alert">
                  <h4 style={{ margin: '0 0 8px 0', fontSize: '0.95rem' }}>📄 What's Included:</h4>
                  <ul style={{ margin: 0, paddingLeft: '18px', lineHeight: '1.7', fontSize: '0.9rem' }}>
                    <li>IRS Notice 2014-7 Acknowledgment & Election Form</li>
                    <li>To Whom It May Concern Declaration</li>
                    <li>Complete Client Information (Taxpayer, Spouse, Dependents)</li>
                    <li>Engagement Letter with Terms & Conditions</li>
                    <li>Photo ID Verification (Consumer & Caregiver)</li>
                    <li>Digital Signature Capture</li>
                  </ul>
                </div>

                <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                  <button
                    onClick={() => {
                      const formUrl = `${window.location.origin}/irs-waiver?company=${company?.id || ''}&taxYear=${new Date().getFullYear() - 1}`;
                      navigator.clipboard.writeText(formUrl);
                      alert('✅ Form link copied to clipboard!\n\nShare this link with your clients to fill out the IRS Waiver form.');
                    }}
                    className="tfp-btn tfp-btn-success" style={{ flex: 1, minWidth: '180px' }}
                  >
                    🔗 Copy Form Link
                  </button>
                  <button
                    onClick={() => {
                      const formUrl = `${window.location.origin}/irs-waiver?company=${company?.id || ''}&taxYear=${new Date().getFullYear() - 1}`;
                      window.open(formUrl, '_blank');
                    }}
                    className="tfp-btn tfp-btn-primary" style={{ flex: 1, minWidth: '200px' }}
                  >
                    👁️ Preview Form
                  </button>
                </div>
              </div>
            </div>

            <hr className="tfp-divider" />

            <div className="tfp-section-header" style={{ marginBottom: '20px' }}>
              <div className="tfp-section-header-left">
                <h2 className="tfp-section-title">📄 All Signed Tax Consent Documents</h2>
              </div>
              <div className="tfp-filters">
                <label style={{ color: 'var(--tfp-text-muted)', fontSize: '0.9rem' }}>Filter by Preparer:</label>
                <select
                  value={selectedPreparerForDocs}
                  onChange={(e) => setSelectedPreparerForDocs(e.target.value)}
                  className="tfp-form-select"
                >
                  <option value="all">All Preparers ({signedConsents.length})</option>
                  {preparers.map(p => {
                    const count = signedConsents.filter(c => c.preparerId === p.id).length;
                    return (
                      <option key={p.id} value={p.id}>
                        {p.name} ({count})
                      </option>
                    );
                  })}
                </select>
              </div>
            </div>

            {/* Stats Summary */}
            <div className="tfp-summary-grid">
              <div className="tfp-summary-box green">
                <div className="tfp-summary-value">{signedConsents.length}</div>
                <div className="tfp-summary-label">Total Documents</div>
              </div>
              <div className="tfp-summary-box blue">
                <div className="tfp-summary-value">
                  {new Set(signedConsents.map(c => c.preparerId)).size}
                </div>
                <div className="tfp-summary-label">Active Preparers</div>
              </div>
              <div className="tfp-summary-box orange">
                <div className="tfp-summary-value">
                  {signedConsents.filter(c => {
                    const signedDate = new Date(c.signedAt);
                    const thirtyDaysAgo = new Date();
                    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
                    return signedDate >= thirtyDaysAgo;
                  }).length}
                </div>
                <div className="tfp-summary-label">Last 30 Days</div>
              </div>
            </div>

            {/* Search and Filter Section */}
            <div className="tfp-search-bar green">
              <div className="tfp-search-bar-inner">
                <div className="tfp-search-field" style={{ flex: 1, minWidth: '180px' }}>
                  <label className="tfp-form-label">🔍 Search by Name</label>
                  <input
                    type="text"
                    placeholder="Client name..."
                    value={completedSearchName}
                    onChange={(e) => setCompletedSearchName(e.target.value)}
                    className="tfp-form-input"
                  />
                </div>
                <div className="tfp-search-field" style={{ minWidth: '140px' }}>
                  <label className="tfp-form-label">📅 From</label>
                  <input type="date" value={completedSearchDateFrom} onChange={(e) => setCompletedSearchDateFrom(e.target.value)} className="tfp-form-input" />
                </div>
                <div className="tfp-search-field" style={{ minWidth: '140px' }}>
                  <label className="tfp-form-label">📅 To</label>
                  <input type="date" value={completedSearchDateTo} onChange={(e) => setCompletedSearchDateTo(e.target.value)} className="tfp-form-input" />
                </div>
                {(completedSearchName || completedSearchDateFrom || completedSearchDateTo) && (
                  <div style={{ display: 'flex', alignItems: 'flex-end' }}>
                    <button onClick={() => { setCompletedSearchName(''); setCompletedSearchDateFrom(''); setCompletedSearchDateTo(''); }} className="tfp-btn tfp-btn-danger tfp-btn-sm">✕ Clear</button>
                  </div>
                )}
              </div>
            </div>

            {/* Selection Actions Bar */}
            {selectedCompletedForms.length > 0 && (
              <div className="tfp-selection-bar">
                <span className="tfp-selection-count">☑️ {selectedCompletedForms.length} selected</span>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  <button
                    onClick={async () => {
                      const selectedSubs = signedConsents.filter(s => selectedCompletedForms.includes(s.id));
                      if (selectedSubs.length === 1) {
                        await downloadIRSWaiverPDF(selectedSubs[0], company, null);
                        setSelectedCompletedForms([]);
                      } else if (selectedSubs.length > 1) {
                        if (!window.confirm(`Download ${selectedSubs.length} forms as ZIP?`)) return;
                        const JSZip = (await import('jszip')).default;
                        const { saveAs } = await import('file-saver');
                        const { generateIRSWaiverPDF } = await import('../pdfGenerator');
                        const zip = new JSZip();
                        for (const sub of selectedSubs) {
                          try {
                            const pdfBytes = await generateIRSWaiverPDF(sub, company, null);
                            const name = (sub.taxpayerInfo?.name || 'Client').replace(/[^a-zA-Z0-9]/g, '_');
                            zip.file(`IRS_Waiver_${name}_TY${sub.taxYear || 'Unknown'}.pdf`, pdfBytes);
                          } catch (e) { console.error(e); }
                        }
                        const blob = await zip.generateAsync({ type: 'blob' });
                        saveAs(blob, `Forms_${selectedSubs.length}_files.zip`);
                        setSelectedCompletedForms([]);
                      }
                    }}
                    className="tfp-btn tfp-btn-success tfp-btn-sm"
                  >
                    📥 {selectedCompletedForms.length === 1 ? 'Download PDF' : `Download ${selectedCompletedForms.length} as ZIP`}
                  </button>
                  <button onClick={() => setSelectedCompletedForms([])} className="tfp-btn tfp-btn-ghost tfp-btn-sm">✕ Clear</button>
                </div>
              </div>
            )}

            {/* Documents List */}
            {(() => {
              let filteredConsents = selectedPreparerForDocs === 'all' 
                ? signedConsents 
                : signedConsents.filter(c => c.preparerId === selectedPreparerForDocs);
              
              // Apply name and date filters
              filteredConsents = filteredConsents.filter(consent => {
                if (completedSearchName && !consent.taxpayerInfo?.name?.toLowerCase().includes(completedSearchName.toLowerCase())) return false;
                const consentDate = new Date(consent.signedAt || consent.createdAt);
                if (completedSearchDateFrom && consentDate < new Date(completedSearchDateFrom)) return false;
                if (completedSearchDateTo) { const to = new Date(completedSearchDateTo); to.setHours(23,59,59,999); if (consentDate > to) return false; }
                return true;
              });
              
              if (filteredConsents.length === 0) {
                return (
                  <div className="tfp-empty-state">
                    <div className="tfp-empty-state-icon">📭</div>
                    <h3 className="tfp-empty-state-title">No Signed Documents</h3>
                    <p className="tfp-empty-state-text">
                      {selectedPreparerForDocs === 'all' 
                        ? 'No consent documents have been signed yet. Preparers can send consent requests to clients from their dashboard.'
                        : 'This preparer has no signed documents yet.'}
                    </p>
                  </div>
                );
              }

              // Group by preparer for display
              const groupedByPreparer = filteredConsents.reduce((acc, consent) => {
                const preparerName = consent.preparerName || 'Unknown Preparer';
                if (!acc[preparerName]) {
                  acc[preparerName] = [];
                }
                acc[preparerName].push(consent);
                return acc;
              }, {});

              return (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '25px' }}>
                  {Object.entries(groupedByPreparer).map(([preparerName, consents]) => (
                    <div key={preparerName}>
                      <h3 className="tfp-doc-group-header">
                        👤 {preparerName}
                        <span className="tfp-badge blue">{consents.length} document{consents.length !== 1 ? 's' : ''}</span>
                      </h3>
                      
                      <div style={{ display: 'grid', gap: '12px' }}>
                        {consents.map(consent => (
                          <div key={consent.id} className={`tfp-doc-row ${selectedCompletedForms.includes(consent.id) ? 'selected' : ''}`}>
                            <div className="tfp-doc-row-left">
                              <input
                                type="checkbox"
                                checked={selectedCompletedForms.includes(consent.id)}
                                onChange={(e) => {
                                  if (e.target.checked) setSelectedCompletedForms(prev => [...prev, consent.id]);
                                  else setSelectedCompletedForms(prev => prev.filter(id => id !== consent.id));
                                }}
                                className="tfp-checkbox"
                              />
                              <div style={{ minWidth: 0 }}>
                                <div className="tfp-doc-name">
                                  {consent.taxpayerInfo?.name || 'Unknown'}
                                </div>
                                <div className="tfp-doc-meta">
                                  TY {consent.taxYear} • {new Date(consent.signedAt).toLocaleDateString()}
                                </div>
                              </div>
                            </div>
                            
                            <div className="tfp-doc-actions">
                              <button
                                onClick={() => downloadIRSWaiverPDF(consent, company, null)}
                                className="tfp-btn tfp-btn-success tfp-btn-sm"
                              >
                                📥 PDF
                              </button>
                              <button
                                onClick={() => setViewingConsent(consent)}
                                className="tfp-btn tfp-btn-primary tfp-btn-sm"
                              >
                                👁️ View
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              );
            })()}
          </div>
        )}
                </ContentCard>
                </motion.div>
              </AnimatePresence>
            </MainContent>
          </>
        );
      })()}

      {/* Add Preparer Modal */}
      {showAddPreparerForm && (
        <div className="tfp-modal-overlay">
          <div className="tfp-modal">
            <div className="tfp-modal-header green">
              <h2>➕ Add New Preparer</h2>
              <button onClick={() => setShowAddPreparerForm(false)} className="tfp-modal-close">×</button>
            </div>
            
            <div className="tfp-modal-body">
              <form onSubmit={handleAddPreparer}>
                
                {/* Photo Upload Section */}
                <div className="tfp-photo-upload green">
                  <label className="tfp-form-label">📷 Profile Photo</label>
                  <div className="tfp-photo-upload-row">
                    <div className={`tfp-photo-circle ${preparerForm.photo ? 'has-photo green' : ''}`}>
                      {preparerForm.photo ? (
                        <img src={preparerForm.photo} alt="Preview" className="tfp-photo-img" />
                      ) : (
                        <span className="tfp-photo-placeholder">👤</span>
                      )}
                    </div>
                    <div className="tfp-photo-actions">
                      <input type="file" accept="image/*" onChange={(e) => handlePhotoUpload(e, 'newPreparer')} id="preparer-photo-upload" style={{ display: 'none' }} />
                      <label htmlFor="preparer-photo-upload" className="tfp-btn tfp-btn-success tfp-btn-sm">
                        📤 {preparerForm.photo ? 'Change' : 'Upload'}
                      </label>
                      {preparerForm.photo && (
                        <button type="button" onClick={() => setPreparerForm(prev => ({ ...prev, photo: '' }))} className="tfp-btn tfp-btn-danger tfp-btn-sm">
                          🗑️ Remove
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                {/* Name Field */}
                <div className="tfp-form-group">
                  <label className="tfp-form-label"><span className="tfp-required">*</span> Full Name</label>
                  <input type="text" value={preparerForm.name} onChange={(e) => setPreparerForm(prev => ({ ...prev, name: e.target.value }))} required placeholder="Full name" className="tfp-form-input" />
                </div>

                {/* Username & Password */}
                <div className="tfp-form-row">
                  <div className="tfp-form-group">
                    <label className="tfp-form-label"><span className="tfp-required">*</span> Username</label>
                    <input type="text" value={preparerForm.username} onChange={(e) => setPreparerForm(prev => ({ ...prev, username: e.target.value }))} required placeholder="Username" className="tfp-form-input" />
                  </div>
                  <div className="tfp-form-group">
                    <label className="tfp-form-label"><span className="tfp-required">*</span> Password</label>
                    <div style={{ position: 'relative' }}>
                      <input type={showPasswordFor['add'] ? "text" : "password"} value={preparerForm.password} onChange={(e) => setPreparerForm(prev => ({ ...prev, password: e.target.value }))} required placeholder="Password" className="tfp-form-input" style={{ paddingRight: '40px' }} />
                      <button type="button" onClick={() => setShowPasswordFor(prev => ({ ...prev, add: !prev.add }))} className="tfp-password-toggle">
                        {showPasswordFor['add'] ? '👁️' : '👁️‍🗨️'}
                      </button>
                    </div>
                  </div>
                </div>

                {/* Email & Phone */}
                <div className="tfp-form-row">
                  <div className="tfp-form-group">
                    <label className="tfp-form-label">📧 Email</label>
                    <input type="email" value={preparerForm.email} onChange={(e) => setPreparerForm(prev => ({ ...prev, email: e.target.value }))} placeholder="Email" className="tfp-form-input" />
                  </div>
                  <div className="tfp-form-group">
                    <label className="tfp-form-label">📞 Phone</label>
                    <input type="tel" value={preparerForm.phone} onChange={(e) => setPreparerForm(prev => ({ ...prev, phone: e.target.value }))} placeholder="Phone" className="tfp-form-input" />
                  </div>
                </div>

                {/* Address */}
                <div className="tfp-form-group">
                  <label className="tfp-form-label">📍 Address</label>
                  <input type="text" value={preparerForm.address} onChange={(e) => setPreparerForm(prev => ({ ...prev, address: e.target.value }))} placeholder="Address (optional)" className="tfp-form-input" />
                </div>

                {/* Bio */}
                <div className="tfp-form-group">
                  <label className="tfp-form-label">📝 Bio</label>
                  <textarea value={preparerForm.bio} onChange={(e) => setPreparerForm(prev => ({ ...prev, bio: e.target.value }))} placeholder="Professional bio..." rows="3" maxLength={300} className="tfp-form-input" style={{ resize: 'vertical', fontFamily: 'inherit' }} />
                  <div className="tfp-char-count">{preparerForm.bio?.length || 0}/300</div>
                </div>

                {/* Action Buttons */}
                <div className="tfp-modal-footer">
                  <button type="button" onClick={() => setShowAddPreparerForm(false)} className="tfp-btn tfp-btn-ghost">Cancel</button>
                  <button type="submit" className="tfp-btn tfp-btn-success">✓ Add Preparer</button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Edit Preparer Modal */}
      {showEditPreparerForm && editingPreparer && (
        <div className="tfp-modal-overlay">
          <div className="tfp-modal">
            <div className="tfp-modal-header blue">
              <h2>✏️ Edit Preparer</h2>
              <button onClick={() => { setShowEditPreparerForm(false); setEditingPreparer(null); }} className="tfp-modal-close">×</button>
            </div>
            
            <div className="tfp-modal-body">
              <form onSubmit={handleUpdatePreparer}>
                
                {/* Photo Upload Section */}
                <div className="tfp-photo-upload blue">
                  <label className="tfp-form-label">📷 Profile Photo</label>
                  <div className="tfp-photo-upload-row">
                    <div className={`tfp-photo-circle ${preparerForm.photo ? 'has-photo blue' : ''}`}>
                      {preparerForm.photo ? (
                        <img src={preparerForm.photo} alt="Preview" className="tfp-photo-img" />
                      ) : (
                        <span className="tfp-photo-placeholder">👤</span>
                      )}
                    </div>
                    <div className="tfp-photo-actions">
                      <input type="file" accept="image/*" onChange={(e) => handlePhotoUpload(e, 'editPreparer')} id="edit-preparer-photo-upload" style={{ display: 'none' }} />
                      <label htmlFor="edit-preparer-photo-upload" className="tfp-btn tfp-btn-primary tfp-btn-sm">
                        📤 {preparerForm.photo ? 'Change' : 'Upload'}
                      </label>
                      {preparerForm.photo && (
                        <button type="button" onClick={() => setPreparerForm(prev => ({ ...prev, photo: '' }))} className="tfp-btn tfp-btn-danger tfp-btn-sm">
                          🗑️ Remove
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                {/* Name Field */}
                <div className="tfp-form-group">
                  <label className="tfp-form-label"><span className="tfp-required">*</span> Full Name</label>
                  <input type="text" value={preparerForm.name} onChange={(e) => setPreparerForm(prev => ({ ...prev, name: e.target.value }))} required placeholder="Full name" className="tfp-form-input" />
                </div>

                {/* Username & Password */}
                <div className="tfp-form-row">
                  <div className="tfp-form-group">
                    <label className="tfp-form-label"><span className="tfp-required">*</span> Username</label>
                    <input type="text" value={preparerForm.username} onChange={(e) => setPreparerForm(prev => ({ ...prev, username: e.target.value }))} required placeholder="Username" className="tfp-form-input" />
                  </div>
                  <div className="tfp-form-group">
                    <label className="tfp-form-label"><span className="tfp-required">*</span> Password</label>
                    <div style={{ position: 'relative' }}>
                      <input type={showPasswordFor['edit'] ? "text" : "password"} value={preparerForm.password} onChange={(e) => setPreparerForm(prev => ({ ...prev, password: e.target.value }))} required placeholder="Password" className="tfp-form-input" style={{ paddingRight: '40px' }} />
                      <button type="button" onClick={() => setShowPasswordFor(prev => ({ ...prev, edit: !prev.edit }))} className="tfp-password-toggle">
                        {showPasswordFor['edit'] ? '👁️' : '👁️‍🗨️'}
                      </button>
                    </div>
                  </div>
                </div>

                {/* Email & Phone */}
                <div className="tfp-form-row">
                  <div className="tfp-form-group">
                    <label className="tfp-form-label">📧 Email</label>
                    <input type="email" value={preparerForm.email} onChange={(e) => setPreparerForm(prev => ({ ...prev, email: e.target.value }))} placeholder="Email" className="tfp-form-input" />
                  </div>
                  <div className="tfp-form-group">
                    <label className="tfp-form-label">📞 Phone</label>
                    <input type="tel" value={preparerForm.phone} onChange={(e) => setPreparerForm(prev => ({ ...prev, phone: e.target.value }))} placeholder="Phone" className="tfp-form-input" />
                  </div>
                </div>

                {/* Address */}
                <div className="tfp-form-group">
                  <label className="tfp-form-label">📍 Address</label>
                  <input type="text" value={preparerForm.address} onChange={(e) => setPreparerForm(prev => ({ ...prev, address: e.target.value }))} placeholder="Address (optional)" className="tfp-form-input" />
                </div>

                {/* Bio */}
                <div className="tfp-form-group">
                  <label className="tfp-form-label">📝 Bio</label>
                  <textarea value={preparerForm.bio} onChange={(e) => setPreparerForm(prev => ({ ...prev, bio: e.target.value }))} placeholder="Professional bio..." rows="3" maxLength={300} className="tfp-form-input" style={{ resize: 'vertical', fontFamily: 'inherit' }} />
                  <div className="tfp-char-count">{preparerForm.bio?.length || 0}/300</div>
                </div>

                {/* Action Buttons */}
                <div className="tfp-modal-footer">
                  <button type="button" onClick={() => { setShowEditPreparerForm(false); setEditingPreparer(null); }} className="tfp-btn tfp-btn-ghost">Cancel</button>
                  <button type="submit" className="tfp-btn tfp-btn-success">✓ Update</button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Company Settings Modal */}
      {showCompanySettings && (
        <div className="tfp-modal-overlay" style={{ overflowY: 'auto' }}>
          <div className="tfp-modal tfp-modal-lg">
            <div className="tfp-modal-header" style={{ position: 'sticky', top: 0, zIndex: 10 }}>
              <h2>⚙️ Company Settings</h2>
              <button className="tfp-modal-close" onClick={() => setShowCompanySettings(false)}>×</button>
            </div>
            <form onSubmit={handleUpdateCompanySettings} className="tfp-modal-body" style={{ maxHeight: 'calc(90vh - 70px)', overflowY: 'auto' }}>
              {/* Company Information Section */}
              <div className="tfp-settings-grid">
                <div>
                  <h3 className="tfp-settings-section-title">📋 Company Information</h3>
                  
                  <div className="tfp-form-group">
                    <label className="tfp-form-label">Company Name</label>
                    <input
                      type="text"
                      value={companySettings.name}
                      onChange={(e) => setCompanySettings(prev => ({ ...prev, name: e.target.value }))}
                      className="tfp-form-input"
                    />
                  </div>
                  
                  <div className="tfp-form-group">
                    <label className="tfp-form-label">📧 Email</label>
                    <input
                      type="email"
                      value={companySettings.email}
                      onChange={(e) => setCompanySettings(prev => ({ ...prev, email: e.target.value }))}
                      className="tfp-form-input"
                    />
                  </div>
                  
                  <div className="tfp-form-group">
                    <label className="tfp-form-label">📞 Phone</label>
                    <input
                      type="tel"
                      value={companySettings.phone}
                      onChange={(e) => setCompanySettings(prev => ({ ...prev, phone: e.target.value }))}
                      className="tfp-form-input"
                    />
                  </div>
                  
                  <div className="tfp-form-group">
                    <label className="tfp-form-label">📍 Address</label>
                    <input
                      type="text"
                      value={companySettings.address}
                      onChange={(e) => setCompanySettings(prev => ({ ...prev, address: e.target.value }))}
                      className="tfp-form-input"
                    />
                  </div>
                  
                  <div className="tfp-form-group">
                    <label className="tfp-form-label">📝 Description</label>
                    <textarea
                      value={companySettings.description}
                      onChange={(e) => setCompanySettings(prev => ({ ...prev, description: e.target.value }))}
                      rows="4"
                      className="tfp-form-input" style={{ resize: 'vertical' }}
                    />
                  </div>
                </div>

                <div>
                  <h3 className="tfp-settings-section-title">🎨 Branding</h3>
                  
                  <div className="tfp-form-group">
                    <label className="tfp-form-label">🖼️ Logo</label>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => {
                        const file = e.target.files[0];
                        if (file) {
                          const reader = new FileReader();
                          reader.onloadend = () => {
                            setCompanySettings(prev => ({ ...prev, logo: reader.result }));
                          };
                          reader.readAsDataURL(file);
                        }
                      }}
                      className="tfp-form-input"
                    />
                    {companySettings.logo && (
                      <div style={{ marginTop: '10px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <img src={companySettings.logo} alt="Logo" style={{ width: '60px', height: '60px', objectFit: 'contain', borderRadius: '8px', border: '1px solid var(--tfp-border)' }} />
                        <button type="button" onClick={() => setCompanySettings(prev => ({ ...prev, logo: '' }))} className="tfp-btn tfp-btn-danger tfp-btn-sm">
                          Remove
                        </button>
                      </div>
                    )}
                  </div>
                  
                  <div className="tfp-color-grid">
                    <div className="tfp-form-group">
                      <label className="tfp-form-label">Primary Color</label>
                      <input
                        type="color"
                        value={companySettings.primaryColor}
                        onChange={(e) => setCompanySettings(prev => ({ ...prev, primaryColor: e.target.value }))}
                        className="tfp-color-input"
                      />
                    </div>
                    <div className="tfp-form-group">
                      <label className="tfp-form-label">Secondary Color</label>
                      <input
                        type="color"
                        value={companySettings.secondaryColor}
                        onChange={(e) => setCompanySettings(prev => ({ ...prev, secondaryColor: e.target.value }))}
                        className="tfp-color-input"
                      />
                    </div>
                    <div className="tfp-form-group">
                      <label className="tfp-form-label">Accent Color</label>
                      <input
                        type="color"
                        value={companySettings.accentColor}
                        onChange={(e) => setCompanySettings(prev => ({ ...prev, accentColor: e.target.value }))}
                        className="tfp-color-input"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Landing Page Customization */}
              <div style={{ marginTop: '20px' }}>
                <h3 className="tfp-settings-section-title">🎨 Landing Page Customization</h3>
                <div className="form-row">
                    <div className="form-group">
                      <label>Hero Title (leave blank for company name)</label>
                      <input
                        type="text"
                        value={companySettings.heroTitle}
                        onChange={(e) => setCompanySettings(prev => ({ ...prev, heroTitle: e.target.value }))}
                        placeholder={companySettings.name || 'Your Company Name'}
                      />
                    </div>
                    <div className="form-group">
                      <label>Hero Subtitle / Tagline</label>
                      <input
                        type="text"
                        value={companySettings.heroSubtitle}
                        onChange={(e) => setCompanySettings(prev => ({ ...prev, heroSubtitle: e.target.value }))}
                        placeholder="Professional Tax Preparation Services"
                      />
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Preparers Section Title</label>
                      <input
                        type="text"
                        value={companySettings.preparersSectionTitle}
                        onChange={(e) => setCompanySettings(prev => ({ ...prev, preparersSectionTitle: e.target.value }))}
                        placeholder="Our Tax Preparers"
                      />
                    </div>
                    <div className="form-group">
                      <label>Preparers Section Subtitle</label>
                      <input
                        type="text"
                        value={companySettings.preparersSectionSubtitle}
                        onChange={(e) => setCompanySettings(prev => ({ ...prev, preparersSectionSubtitle: e.target.value }))}
                        placeholder="Choose your preferred preparer"
                      />
                    </div>
                  </div>
                  <h4 style={{marginTop: '15px', marginBottom: '10px'}}>Why Choose Us Section</h4>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Feature 1 Title</label>
                      <input
                        type="text"
                        value={companySettings.feature1Title}
                        onChange={(e) => setCompanySettings(prev => ({ ...prev, feature1Title: e.target.value }))}
                        placeholder="Expert Preparers"
                      />
                    </div>
                    <div className="form-group">
                      <label>Feature 1 Description</label>
                      <input
                        type="text"
                        value={companySettings.feature1Text}
                        onChange={(e) => setCompanySettings(prev => ({ ...prev, feature1Text: e.target.value }))}
                        placeholder="Certified professionals with years of experience"
                      />
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Feature 2 Title</label>
                      <input
                        type="text"
                        value={companySettings.feature2Title}
                        onChange={(e) => setCompanySettings(prev => ({ ...prev, feature2Title: e.target.value }))}
                        placeholder="Fast Service"
                      />
                    </div>
                    <div className="form-group">
                      <label>Feature 2 Description</label>
                      <input
                        type="text"
                        value={companySettings.feature2Text}
                        onChange={(e) => setCompanySettings(prev => ({ ...prev, feature2Text: e.target.value }))}
                        placeholder="Quick and efficient tax preparation"
                      />
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Feature 3 Title</label>
                      <input
                        type="text"
                        value={companySettings.feature3Title}
                        onChange={(e) => setCompanySettings(prev => ({ ...prev, feature3Title: e.target.value }))}
                        placeholder="Secure Filing"
                      />
                    </div>
                    <div className="form-group">
                      <label>Feature 3 Description</label>
                      <input
                        type="text"
                        value={companySettings.feature3Text}
                        onChange={(e) => setCompanySettings(prev => ({ ...prev, feature3Text: e.target.value }))}
                        placeholder="Your information is protected"
                      />
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Footer Text</label>
                    <input
                      type="text"
                      value={companySettings.footerText}
                      onChange={(e) => setCompanySettings(prev => ({ ...prev, footerText: e.target.value }))}
                      placeholder="© 2025 Your Company. All rights reserved."
                    />
                  </div>
              </div>

              <div className="tfp-modal-footer" style={{ marginTop: '30px' }}>
                <button type="button" onClick={() => setShowCompanySettings(false)} className="tfp-btn tfp-btn-ghost">Cancel</button>
                <button type="submit" className="tfp-btn tfp-btn-primary">{savingSettings ? 'Saving...' : '✅ Save Settings'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* View Signed Consent Modal (Admin) */}
      {viewingConsent && (
        <div className="tfp-modal-overlay">
          <div className="tfp-modal tfp-modal-lg">
            <div className="tfp-modal-header green" style={{ position: 'sticky', top: 0, zIndex: 10 }}>
              <h2>📋 Signed Consent Document</h2>
              <button onClick={() => setViewingConsent(null)} className="tfp-modal-close">×</button>
            </div>
            
            <div className="tfp-modal-body">
              {/* Preparer Info */}
              <div style={{ background: '#f0fdf4', padding: '12px 15px', borderRadius: '10px', marginBottom: '15px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                <span style={{ fontSize: '1.2rem' }}>👤</span>
                <div>
                  <span style={{ color: '#166534', fontSize: '0.85rem' }}>Prepared by: </span>
                  <span style={{ color: '#1e293b', fontWeight: '600' }}>{viewingConsent.preparerName}</span>
                </div>
              </div>

              {/* Taxpayer Information */}
              <div style={{ background: '#f8fafc', padding: '15px', borderRadius: '12px', marginBottom: '20px' }}>
                <h3 style={{ margin: '0 0 12px 0', color: '#1e293b', fontSize: '1rem' }}>👤 Taxpayer Information</h3>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '12px' }}>
                  <div>
                    <span style={{ color: '#64748b', fontSize: '0.8rem', display: 'block' }}>Name</span>
                    <span style={{ color: '#1e293b', fontWeight: '500' }}>{viewingConsent.taxpayerInfo?.name}</span>
                  </div>
                  <div>
                    <span style={{ color: '#64748b', fontSize: '0.8rem', display: 'block' }}>Email</span>
                    <span style={{ color: '#1e293b', fontWeight: '500' }}>{viewingConsent.taxpayerInfo?.email}</span>
                  </div>
                  {viewingConsent.taxpayerInfo?.phone && (
                    <div>
                      <span style={{ color: '#64748b', fontSize: '0.8rem', display: 'block' }}>Phone</span>
                      <span style={{ color: '#1e293b', fontWeight: '500' }}>{viewingConsent.taxpayerInfo.phone}</span>
                    </div>
                  )}
                  {viewingConsent.taxpayerInfo?.ssn && (
                    <div>
                      <span style={{ color: '#64748b', fontSize: '0.8rem', display: 'block' }}>SSN (Last 4)</span>
                      <span style={{ color: '#1e293b', fontWeight: '500' }}>***-**-{viewingConsent.taxpayerInfo.ssn}</span>
                    </div>
                  )}
                  {viewingConsent.taxpayerInfo?.address && (
                    <div style={{ gridColumn: 'span 2' }}>
                      <span style={{ color: '#64748b', fontSize: '0.8rem', display: 'block' }}>Address</span>
                      <span style={{ color: '#1e293b', fontWeight: '500' }}>{viewingConsent.taxpayerInfo.address}</span>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Document Details */}
              <div style={{ background: '#eff6ff', padding: '15px', borderRadius: '12px', marginBottom: '20px' }}>
                <h3 style={{ margin: '0 0 12px 0', color: '#1d4ed8', fontSize: '1rem' }}>📄 Document Details</h3>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '12px' }}>
                  <div>
                    <span style={{ color: '#1d4ed8', fontSize: '0.8rem', display: 'block' }}>Tax Year</span>
                    <span style={{ color: '#1e293b', fontWeight: '500' }}>{viewingConsent.taxYear}</span>
                  </div>
                  <div>
                    <span style={{ color: '#1d4ed8', fontSize: '0.8rem', display: 'block' }}>Signed Date</span>
                    <span style={{ color: '#1e293b', fontWeight: '500' }}>
                      {viewingConsent.signedAt ? new Date(viewingConsent.signedAt).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      }) : 'N/A'}
                    </span>
                  </div>
                  <div>
                    <span style={{ color: '#1d4ed8', fontSize: '0.8rem', display: 'block' }}>Document ID</span>
                    <span style={{ color: '#64748b', fontWeight: '500', fontSize: '0.85rem' }}>{viewingConsent.id?.substring(0, 12)}...</span>
                  </div>
                </div>
              </div>
              
              {/* Consents Given */}
              <div style={{ background: '#f8fafc', padding: '15px', borderRadius: '12px', marginBottom: '20px' }}>
                <h3 style={{ margin: '0 0 12px 0', color: '#374151', fontSize: '1rem' }}>✅ Consents Given</h3>
                <div style={{ display: 'grid', gap: '8px' }}>
                  {viewingConsent.consents?.disclosure && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 12px', background: 'white', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
                      <span style={{ color: '#10b981' }}>✓</span>
                      <span style={{ color: '#1e293b', fontSize: '0.9rem' }}>IRC §7216 Disclosure Consent</span>
                    </div>
                  )}
                  {viewingConsent.consents?.useConsent && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 12px', background: 'white', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
                      <span style={{ color: '#10b981' }}>✓</span>
                      <span style={{ color: '#1e293b', fontSize: '0.9rem' }}>Consent to Use Tax Return Information</span>
                    </div>
                  )}
                  {viewingConsent.consents?.efileAuth && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 12px', background: 'white', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
                      <span style={{ color: '#10b981' }}>✓</span>
                      <span style={{ color: '#1e293b', fontSize: '0.9rem' }}>IRS E-File Signature Authorization</span>
                    </div>
                  )}
                  {viewingConsent.consents?.incomeWaiver && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 12px', background: 'white', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
                      <span style={{ color: '#f59e0b' }}>📝</span>
                      <span style={{ color: '#1e293b', fontSize: '0.9rem' }}>Income Documentation Waiver</span>
                    </div>
                  )}
                  {viewingConsent.consents?.privacyPolicy && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 12px', background: 'white', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
                      <span style={{ color: '#10b981' }}>✓</span>
                      <span style={{ color: '#1e293b', fontSize: '0.9rem' }}>Privacy Policy Acknowledgment</span>
                    </div>
                  )}
                  {viewingConsent.consents?.feeAgreement && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 12px', background: 'white', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
                      <span style={{ color: '#10b981' }}>💰</span>
                      <span style={{ color: '#1e293b', fontSize: '0.9rem' }}>Fee Agreement Accepted</span>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Signature */}
              <div style={{ background: '#fefce8', padding: '15px', borderRadius: '12px', marginBottom: '20px' }}>
                <h3 style={{ margin: '0 0 12px 0', color: '#92400e', fontSize: '1rem' }}>✍️ Electronic Signature</h3>
                {viewingConsent.signature?.image && (
                  <div style={{ background: 'white', padding: '15px', borderRadius: '8px', border: '1px solid #fcd34d', marginBottom: '10px' }}>
                    <img 
                      src={viewingConsent.signature.image} 
                      alt="Signature" 
                      style={{ maxWidth: '100%', maxHeight: '100px' }}
                    />
                  </div>
                )}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '20px', fontSize: '0.85rem' }}>
                  <div>
                    <span style={{ color: '#92400e' }}>Typed Name: </span>
                    <span style={{ color: '#1e293b', fontWeight: '500' }}>{viewingConsent.signature?.typed}</span>
                  </div>
                  <div>
                    <span style={{ color: '#92400e' }}>IP Address: </span>
                    <span style={{ color: '#64748b' }}>{viewingConsent.signature?.ipAddress || 'N/A'}</span>
                  </div>
                </div>
              </div>
              
              {/* Print Button */}
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                <button 
                  onClick={() => {
                    const printWindow = window.open('', '_blank');
                    printWindow.document.write(`
                      <html>
                        <head>
                          <title>Tax Consent Document - ${viewingConsent.taxpayerInfo?.name}</title>
                          <style>
                            body { font-family: Arial, sans-serif; padding: 40px; max-width: 800px; margin: 0 auto; }
                            h1 { color: #1e293b; border-bottom: 2px solid #3b82f6; padding-bottom: 10px; }
                            h2 { color: #374151; margin-top: 25px; }
                            .section { margin-bottom: 20px; padding: 15px; background: #f8fafc; border-radius: 8px; }
                            .consent-item { padding: 8px 0; border-bottom: 1px solid #e5e7eb; }
                            .signature-area { border: 1px solid #d1d5db; padding: 20px; margin-top: 20px; text-align: center; }
                            .footer { margin-top: 40px; padding-top: 20px; border-top: 1px solid #e5e7eb; font-size: 12px; color: #64748b; }
                          </style>
                        </head>
                        <body>
                          <h1>Tax Consent Document</h1>
                          <p><strong>Preparer:</strong> ${viewingConsent.preparerName} | <strong>Company:</strong> ${company?.name || 'N/A'}</p>
                          <div class="section">
                            <h2>Taxpayer Information</h2>
                            <p><strong>Name:</strong> ${viewingConsent.taxpayerInfo?.name}</p>
                            <p><strong>Email:</strong> ${viewingConsent.taxpayerInfo?.email}</p>
                            ${viewingConsent.taxpayerInfo?.phone ? `<p><strong>Phone:</strong> ${viewingConsent.taxpayerInfo.phone}</p>` : ''}
                            ${viewingConsent.taxpayerInfo?.address ? `<p><strong>Address:</strong> ${viewingConsent.taxpayerInfo.address}</p>` : ''}
                            <p><strong>Tax Year:</strong> ${viewingConsent.taxYear}</p>
                          </div>
                          <div class="section">
                            <h2>Consents Given</h2>
                            ${viewingConsent.consents?.disclosure ? '<div class="consent-item">✓ IRC §7216 Disclosure Consent</div>' : ''}
                            ${viewingConsent.consents?.useConsent ? '<div class="consent-item">✓ Consent to Use Tax Return Information</div>' : ''}
                            ${viewingConsent.consents?.efileAuth ? '<div class="consent-item">✓ IRS E-File Signature Authorization</div>' : ''}
                            ${viewingConsent.consents?.incomeWaiver ? '<div class="consent-item">⚠️ Income Documentation Waiver</div>' : ''}
                            ${viewingConsent.consents?.privacyPolicy ? '<div class="consent-item">✓ Privacy Policy Acknowledgment</div>' : ''}
                            ${viewingConsent.consents?.feeAgreement ? '<div class="consent-item">✓ Fee Agreement Accepted</div>' : ''}
                          </div>
                          <div class="signature-area">
                            <h2>Electronic Signature</h2>
                            ${viewingConsent.signature?.image ? `<img src="${viewingConsent.signature.image}" style="max-width: 300px; margin: 15px 0;" />` : ''}
                            <p><strong>Typed Name:</strong> ${viewingConsent.signature?.typed}</p>
                            <p><strong>Signed Date:</strong> ${viewingConsent.signedAt ? new Date(viewingConsent.signedAt).toLocaleString() : 'N/A'}</p>
                          </div>
                          <div class="footer">
                            <p>Document ID: ${viewingConsent.id}</p>
                            <p>This document was electronically signed and is legally binding.</p>
                          </div>
                        </body>
                      </html>
                    `);
                    printWindow.document.close();
                    printWindow.print();
                  }}
                  style={{ padding: '10px 20px', background: '#f1f5f9', color: '#374151', border: 'none', borderRadius: '8px', fontSize: '0.9rem', fontWeight: '500', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}
                >
                  🖨️ Print Document
                </button>
                <button 
                  onClick={() => setViewingConsent(null)}
                  style={{ padding: '10px 20px', background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)', color: 'white', border: 'none', borderRadius: '8px', fontSize: '0.9rem', fontWeight: '600', cursor: 'pointer' }}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Switch to Preparer Modal */}
      {showPreparerSwitchModal && (
        <div className="tfp-modal-overlay">
          <div className="tfp-modal" style={{ maxWidth: '400px' }}>
            <div className="tfp-modal-header purple">
              <h2>👤 Switch to Preparer</h2>
              <button onClick={() => setShowPreparerSwitchModal(false)} className="tfp-modal-close">×</button>
            </div>
            
            <div className="tfp-modal-body">
              <p style={{ margin: '0 0 12px 0', color: 'var(--tfp-text-muted)', fontSize: '0.85rem' }}>
                Select a preparer to view their dashboard:
              </p>
              
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {preparers.filter(p => p.active).map(preparer => (
                  <button
                    key={preparer.id}
                    onClick={() => {
                      setShowPreparerSwitchModal(false);
                      if (onSwitchToPreparer) {
                        onSwitchToPreparer(preparer);
                      }
                    }}
                    className="tfp-preparer-switch-btn"
                  >
                    {preparer.photo ? (
                      <img src={preparer.photo} alt={preparer.name} style={{ width: '40px', height: '40px', borderRadius: '50%', objectFit: 'cover', border: '2px solid var(--tfp-border)', flexShrink: 0 }} />
                    ) : (
                      <div style={{ width: '40px', height: '40px', borderRadius: '50%', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: '1rem', flexShrink: 0 }}>👤</div>
                    )}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: '600', color: 'var(--tfp-text-primary)', fontSize: '0.9rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{preparer.name}</div>
                      <div style={{ color: 'var(--tfp-text-muted)', fontSize: '0.75rem' }}>@{preparer.username}</div>
                    </div>
                    <span style={{ color: 'var(--tfp-primary)', fontSize: '1.1rem', flexShrink: 0 }}>→</span>
                  </button>
                ))}
              </div>
              
              {preparers.filter(p => p.active).length === 0 && (
                <div className="tfp-empty-state" style={{ padding: '24px' }}>
                  <div className="tfp-empty-state-icon">👥</div>
                  <p className="tfp-empty-state-text">No active preparers</p>
                </div>
              )}
            </div>
            
            <div className="tfp-modal-footer">
              <button onClick={() => setShowPreparerSwitchModal(false)} className="tfp-btn tfp-btn-ghost" style={{ width: '100%' }}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CompanyAdminDashboard;
